import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faChevronDown,
  faChevronRight,
  faPencil,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import SearchableAddDropdown from "../components/SearchableAddDropdown";
import InlineFieldTooltip from "../components/InlineFieldTooltip";
import GeneralCostEditorForm, {
  createEmptyGeneralCostForm,
  GENERAL_COST_CALC_TYPES,
} from "../components/GeneralCostEditorForm";
import {
  buildGeneralCostFormulaPreviewText,
  calculateGeneralCostBaseTotal,
  calculateTeamWeeklyMetrics,
  resolveTeamsAndWeeks,
} from "../utils/mobilizationSimulationUtils";
import {
  MIN_ADJUSTMENT_PERCENT,
  MAX_ADJUSTMENT_PERCENT,
  buildAdjustmentGrid,
  isAllowedAdjustmentText,
  isValidAdjustmentPercent,
} from "../utils/productivityAdjustments";
import {
  buildGeneratedTeamName,
  getAllocationJob,
  getFallbackJobIdentifierFromName,
  getJobIdentifier,
  getJobSortOrder,
  getTeamDisplayName,
  sanitizeJobIdentifierInput,
  sortGroupedMobilizationConfigs,
  sortGroupedTeamConfigs,
  sortTeamAllocationRows,
} from "../utils/mobilizationTeamConfigUtils";

const TABS = [
  { id: "painters-cost", name: "Man-Hour Cost" },
  { id: "painter-productivity", name: "Painter Productivity" },
  { id: "productivity-adjustments", name: "Productivity Adjustments" },
  { id: "general-costs", name: "General Costs" },
  { id: "mobilization-costs", name: "Mobilization Costs" },
];

const SPEED_BANDS = ["Slow", "Medium", "Fast"];
const APPLICATION_TYPES = ["Brush", "Roll", "Spray"];
const SHOW_STANDALONE_MOBILIZATION_SIMULATION = false;
const DEFAULT_SIMULATION_DAMAGED_AREA_SQM_BY_INDEX = [5000, 3000, 2000];

const getDefaultSimulationDamagedAreaSqm = (index) =>
  DEFAULT_SIMULATION_DAMAGED_AREA_SQM_BY_INDEX[index] ?? 1000;

const normalizeSimulationDamagedAreaSqm = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";

  const numericValue = Number(rawValue);
  if (!Number.isFinite(numericValue)) return "";

  return String(Math.max(0, Math.round(numericValue)));
};

const SYSTEM_LIST_KEYS = {
  coatType: "coat_type",
  applicationType: "application_type",
  accessMethods: "access_methods",
};

const ACCESS_METHOD_LIST_NAME = "Access Methods";
const DEFAULT_ACCESS_METHOD_LIST_META = {
  listId: "",
  listName: ACCESS_METHOD_LIST_NAME,
  listKey: SYSTEM_LIST_KEYS.accessMethods,
};

const APPLICATION_TYPE_LIST_NAME = "Application Types";

const normalizeListName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const normalizeMethodKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const normalizeRepairProcessName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const getPaintLayerDisplayName = (processName) => {
  const normalized = normalizeRepairProcessName(processName).replace(
    /\s+/g,
    "",
  );

  if (normalized.includes("prime")) return "Prime";
  if (normalized.includes("strip")) return "Stripecoat";
  if (normalized.includes("mid")) return "Midcoat";
  if (normalized.includes("top")) return "Topcoat";
  return "";
};

const getRepairRuleLayerProportion = (rule) => {
  const rawValue = rule?.stripingProportion ?? rule?.striping_proportion;
  if (rawValue === null || rawValue === undefined || rawValue === "") {
    return 100;
  }

  const numericValue = Number(rawValue);
  if (!Number.isFinite(numericValue)) {
    return 100;
  }

  return numericValue;
};

const hasRepairRuleLayerProportion = (rule) => {
  const rawValue = rule?.stripingProportion ?? rule?.striping_proportion;
  return rawValue !== null && rawValue !== undefined && rawValue !== "";
};

const formatMethodLabel = (value) =>
  String(value || "")
    .trim()
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");

const createEmptySpeedSqftRows = (applicationTypeKeys = []) => {
  const normalizedKeys =
    applicationTypeKeys.length > 0
      ? applicationTypeKeys
          .map((key) => normalizeMethodKey(key))
          .filter(Boolean)
      : APPLICATION_TYPES.map((type) => normalizeMethodKey(type));

  const emptyBand = normalizedKeys.reduce((acc, key) => {
    acc[key] = "";
    return acc;
  }, {});

  return {
    Slow: { ...emptyBand },
    Medium: { ...emptyBand },
    Fast: { ...emptyBand },
  };
};

const SQFT_TO_SQM = 0.09290304;

const createEmptyMobilizationConfigForm = (year) => ({
  year,
  team_id: "",
  speed_band: "Medium",
  selected_general_cost_ids: [],
  is_active: false,
  sort_order: "99",
  notes: "",
});

const EMPTY_JOB_FORM = {
  year: new Date().getFullYear(),
  job_name: "",
  job_identifier: "",
  rate_per_hour: "",
  currency: "CAD",
  converted_rate_per_hour: "",
  hours_per_week: "",
  week_count: "",
  painting_time_pct: "100",
  sort_order: "99",
};

const EMPTY_ALLOCATION = { job_id: "", member_count: "1" };
const EMPTY_TEAM_FORM = {
  year: new Date().getFullYear(),
  sort_order: "99",
  allocations: [{ ...EMPTY_ALLOCATION }],
};

const getJobDisplayName = (job) => {
  const identifier = getJobIdentifier(job);
  const jobName = String(job?.jobName ?? job?.job_name ?? "").trim();

  if (!jobName) return identifier;
  return identifier ? `${identifier} - ${jobName}` : jobName;
};

const areAllocationRowsEqual = (leftRows, rightRows) =>
  leftRows.length === rightRows.length &&
  leftRows.every((row, index) => {
    const rightRow = rightRows[index] || {};
    return (
      String(row?.job_id ?? row?.jobId ?? "") ===
        String(rightRow?.job_id ?? rightRow?.jobId ?? "") &&
      String(row?.member_count ?? row?.memberCount ?? "") ===
        String(rightRow?.member_count ?? rightRow?.memberCount ?? "")
    );
  });

const resolveTeamIdFromValue = (value) => {
  const parsed = Number(
    value?.id ?? value?.teamId ?? value?.team_id ?? value ?? NaN,
  );
  return Number.isFinite(parsed) ? parsed : null;
};

const toNum = (value) => Number(value || 0);
const toInt = (value) => {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : 0;
};
const formatMoney = (value, decimals = 2) =>
  Number(value || 0).toFixed(decimals);
const formatWholeNumber = (value) =>
  Math.round(Number(value || 0)).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  });
const normalizeProductivityAdjustmentPercent = (value) => {
  if (!isValidAdjustmentPercent(value)) return null;
  return Math.round(Number(value) * 100) / 100;
};

const chooseAccessMethodList = (items = []) =>
  items.find((row) => normalizeListName(row.list) === "access methods") ||
  items.find((row) => normalizeListName(row.list) === "access method") ||
  items.find((row) => row.list_key === SYSTEM_LIST_KEYS.accessMethods) ||
  null;

const getAdjustmentMatrixKey = (row) =>
  `${Number(row?.corrosionDamageCombinationId || 0)}|${Number(
    row?.accessMethodSystemListId || 0,
  )}`;

const buildProductivityAdjustmentValueMap = (rows = []) =>
  rows.reduce((acc, row) => {
    const key = getAdjustmentMatrixKey(row);
    if (key !== "0|0") {
      acc[key] = String(
        normalizeProductivityAdjustmentPercent(row.productivityPercentage) ??
          100,
      );
    }
    return acc;
  }, {});

const MobilizationCostsManagement = () => {
  const currentYear = new Date().getFullYear();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(
    searchParams.get("tab") || "painters-cost",
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [jobYears, setJobYears] = useState([]);
  const [teamYears, setTeamYears] = useState([]);
  const [productivityYears, setProductivityYears] = useState([]);
  const [generalCostYears, setGeneralCostYears] = useState([]);
  const [mobilizationConfigYears, setMobilizationConfigYears] = useState([]);
  const [expandedJobYears, setExpandedJobYears] = useState(new Set());
  const [expandedTeamYears, setExpandedTeamYears] = useState(new Set());
  const [expandedProductivityYears, setExpandedProductivityYears] = useState(
    new Set(),
  );
  const [expandedGeneralCostYears, setExpandedGeneralCostYears] = useState(
    new Set(),
  );
  const [expandedMobilizationConfigYears, setExpandedMobilizationConfigYears] =
    useState(new Set());

  const [defaultSystemCurrency, setDefaultSystemCurrency] = useState("CAD");
  const [convertingJobRate, setConvertingJobRate] = useState(false);

  const [showJobModal, setShowJobModal] = useState(false);
  const [savingJob, setSavingJob] = useState(false);
  const [editingJobId, setEditingJobId] = useState(null);
  const [jobForm, setJobForm] = useState({
    ...EMPTY_JOB_FORM,
    year: currentYear,
  });

  const [showTeamModal, setShowTeamModal] = useState(false);
  const [savingTeam, setSavingTeam] = useState(false);
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [teamForm, setTeamForm] = useState({
    ...EMPTY_TEAM_FORM,
    year: currentYear,
  });

  const [showCopyJobModal, setShowCopyJobModal] = useState(false);
  const [copyJobsSourceYear, setCopyJobsSourceYear] = useState(null);
  const [copyJobsTargetYear, setCopyJobsTargetYear] = useState("");
  const [copyJobsError, setCopyJobsError] = useState("");
  const [copyingJobs, setCopyingJobs] = useState(false);
  const [showCopyTeamModal, setShowCopyTeamModal] = useState(false);
  const [copyTeamsSourceYear, setCopyTeamsSourceYear] = useState(null);
  const [copyTeamsTargetYear, setCopyTeamsTargetYear] = useState("");
  const [copyTeamsError, setCopyTeamsError] = useState("");
  const [copyingTeams, setCopyingTeams] = useState(false);

  const [showCopyProductivityModal, setShowCopyProductivityModal] =
    useState(false);
  const [copyProductivitySourceYear, setCopyProductivitySourceYear] =
    useState(null);
  const [copyProductivityTargetYear, setCopyProductivityTargetYear] =
    useState("");
  const [copyProductivityError, setCopyProductivityError] = useState("");
  const [copyingProductivity, setCopyingProductivity] = useState(false);

  const [showProductivityModal, setShowProductivityModal] = useState(false);
  const [savingProductivity, setSavingProductivity] = useState(false);
  const [editingProductivityKey, setEditingProductivityKey] = useState(null);
  const [productivityForm, setProductivityForm] = useState({
    year: currentYear,
    steel_size: "HSS",
    coat_type: "Prime",
    speed_rows: createEmptySpeedSqftRows(),
  });
  const [coatTypeOptions, setCoatTypeOptions] = useState([]);
  const [applicationTypeOptions, setApplicationTypeOptions] = useState([]);
  const [accessMethodRows, setAccessMethodRows] = useState([]);
  const [accessMethodListMeta, setAccessMethodListMeta] = useState(
    DEFAULT_ACCESS_METHOD_LIST_META,
  );
  const [showAccessMethodModal, setShowAccessMethodModal] =
    useState(false);
  const [editingAccessMethod, setEditingAccessMethod] =
    useState(null);
  const [accessMethodForm, setAccessMethodForm] = useState({
    value: "",
    sort_order: "100",
  });
  const [savingAccessMethod, setSavingAccessMethod] =
    useState(false);
  const [accessMethodError, setAccessMethodError] = useState("");
  const [productivityMethodAdjustmentData, setProductivityMethodAdjustmentData] =
    useState({ damageCombinations: [], accessMethods: [], adjustments: [] });
  const [productivityMethodAdjustmentValues, setProductivityMethodAdjustmentValues] =
    useState({});
  // Last in-range value per cell, so a field emptied mid-edit can be restored
  // on blur instead of being reported as an error.
  const lastValidProductivityAdjustmentRef = useRef({});
  const [loadingProductivityMethodAdjustments, setLoadingProductivityMethodAdjustments] =
    useState(false);
  const [savingProductivityMethodAdjustments, setSavingProductivityMethodAdjustments] =
    useState(false);
  const [productivityMethodAdjustmentError, setProductivityMethodAdjustmentError] =
    useState("");
  const [selectedProductivityMethodFilter, setSelectedProductivityMethodFilter] =
    useState("all");

  const [exposureClassOptions, setExposureClassOptions] = useState([]);
  const [showGeneralCostModal, setShowGeneralCostModal] = useState(false);
  const [savingGeneralCost, setSavingGeneralCost] = useState(false);
  const [editingGeneralCostId, setEditingGeneralCostId] = useState(null);
  const [generalCostForm, setGeneralCostForm] = useState(
    createEmptyGeneralCostForm(currentYear),
  );
  const [generalCostConversionRates, setGeneralCostConversionRates] = useState(
    {},
  );

  const [showCopyGeneralCostModal, setShowCopyGeneralCostModal] =
    useState(false);
  const [copyGeneralCostSourceYear, setCopyGeneralCostSourceYear] =
    useState(null);
  const [copyGeneralCostTargetYear, setCopyGeneralCostTargetYear] =
    useState("");
  const [copyGeneralCostError, setCopyGeneralCostError] = useState("");
  const [copyingGeneralCosts, setCopyingGeneralCosts] = useState(false);

  const [showMobilizationConfigModal, setShowMobilizationConfigModal] =
    useState(false);
  const [savingMobilizationConfig, setSavingMobilizationConfig] =
    useState(false);
  const [editingMobilizationConfigId, setEditingMobilizationConfigId] =
    useState(null);
  const [mobilizationConfigForm, setMobilizationConfigForm] = useState(
    createEmptyMobilizationConfigForm(currentYear),
  );

  const [showCopyMobilizationConfigModal, setShowCopyMobilizationConfigModal] =
    useState(false);
  const [
    copyMobilizationConfigSourceYear,
    setCopyMobilizationConfigSourceYear,
  ] = useState(null);
  const [
    copyMobilizationConfigTargetYear,
    setCopyMobilizationConfigTargetYear,
  ] = useState("");
  const [copyMobilizationConfigError, setCopyMobilizationConfigError] =
    useState("");
  const [copyingMobilizationConfigs, setCopyingMobilizationConfigs] =
    useState(false);

  const [simulationYear, setSimulationYear] = useState(null);
  const [selectedSimulationConfigId, setSelectedSimulationConfigId] =
    useState(null);
  const [
    simulationDamagedAreaSqmByExposureClassCode,
    setSimulationDamagedAreaSqmByExposureClassCode,
  ] = useState({});
  const [simulationWeekLimit, setSimulationWeekLimit] = useState("35");
  const [simulationForemansLimit, setSimulationForemansLimit] = useState(1);
  const [simulationCombinations, setSimulationCombinations] = useState([]);
  const [simulationRepairRules, setSimulationRepairRules] = useState([]);
  const [simulationRepairProcessTypes, setSimulationRepairProcessTypes] =
    useState([]);
  const [simulationDamageSplits, setSimulationDamageSplits] = useState([]);
  const [
    simulationAreaSplitPctByCombinationId,
    setSimulationAreaSplitPctByCombinationId,
  ] = useState({});
  const [
    simulationExposureClassEnabledByCode,
    setSimulationExposureClassEnabledByCode,
  ] = useState({});

  useEffect(() => {
    const tabParam = searchParams.get("tab") || "painters-cost";
    const isValid = TABS.some((tab) => tab.id === tabParam);
    if (!isValid) {
      setSearchParams({ tab: "painters-cost" });
      setActiveTab("painters-cost");
      return;
    }
    setActiveTab(tabParam);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const fetchDefaultCurrency = async () => {
      try {
        const settingsResponse = await api.get("/settings/categories");
        if (settingsResponse.data.success) {
          const settings = settingsResponse.data.data;
          const currency =
            settings.currency_units?.currency?.value ||
            settings.currency_units?.currency?.setting_value ||
            "CAD";
          const configuredWeeksLimit = Number(
            settings.project_defaults?.default_mobilization_weeks_limit?.value,
          );
          const configuredForemansLimit = Number(
            settings.project_defaults?.default_foremans_count?.value,
          );
          const normalizedWeeksLimit =
            Number.isFinite(configuredWeeksLimit) && configuredWeeksLimit > 0
              ? String(Math.round(configuredWeeksLimit))
              : "35";
          const normalizedForemansLimit =
            Number.isFinite(configuredForemansLimit) &&
            configuredForemansLimit >= 0
              ? Math.floor(configuredForemansLimit)
              : 1;
          setDefaultSystemCurrency(currency);
          setSimulationWeekLimit(normalizedWeeksLimit);
          setSimulationForemansLimit(normalizedForemansLimit);
        }
      } catch (err) {
        console.error("Error fetching default currency settings:", err);
      }
    };
    fetchDefaultCurrency();
  }, []);

  const fetchPaintersData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [
        jobsRes,
        teamsRes,
        productivityRes,
        generalCostsRes,
        mobilizationConfigsRes,
      ] = await Promise.all([
        api.get("/mobilization-master/painter-jobs?grouped_by_year=true"),
        api.get(
          "/mobilization-master/painter-team-configs?grouped_by_year=true",
        ),
        api.get("/mobilization-master/productivity?grouped_by_year=true"),
        api.get("/mobilization-master/costs?grouped_by_year=true"),
        api.get(
          "/mobilization-master/mobilization-configs?grouped_by_year=true",
        ),
      ]);

      const jobs = jobsRes.data?.data || [];
      const teams = sortGroupedTeamConfigs(teamsRes.data?.data || []);
      const productivity = productivityRes.data?.data || [];
      const generalCosts = generalCostsRes.data?.data || [];
      const teamsByIdMap = new Map();
      teams.forEach((group) => {
        (group.teams || []).forEach((team) => {
          teamsByIdMap.set(Number(team.id), team);
        });
      });
      const mobilizationConfigs = sortGroupedMobilizationConfigs(
        mobilizationConfigsRes.data?.data || [],
        teamsByIdMap,
      );
      setJobYears(jobs);
      setTeamYears(teams);
      setProductivityYears(productivity);
      setGeneralCostYears(generalCosts);
      setMobilizationConfigYears(mobilizationConfigs);

      setExpandedJobYears((prev) => {
        if (jobs.length > 0 && prev.size === 0) return new Set([jobs[0].year]);
        return prev;
      });
      setExpandedTeamYears((prev) => {
        if (teams.length > 0 && prev.size === 0)
          return new Set([teams[0].year]);
        return prev;
      });
      setExpandedProductivityYears((prev) => {
        if (productivity.length > 0 && prev.size === 0)
          return new Set([productivity[0].year]);
        return prev;
      });
      setExpandedGeneralCostYears((prev) => {
        if (generalCosts.length > 0 && prev.size === 0)
          return new Set([generalCosts[0].year]);
        return prev;
      });
      setExpandedMobilizationConfigYears((prev) => {
        if (mobilizationConfigs.length > 0 && prev.size === 0)
          return new Set([mobilizationConfigs[0].year]);
        return prev;
      });
    } catch (err) {
      console.error(err);
      setError("Failed to fetch mobilization master data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPaintersData();
  }, [fetchPaintersData]);

  const fetchCoatTypeOptions = useCallback(async () => {
    try {
      const response = await api.get("/system-lists", {
        params: {
          list: "Coat Type",
          list_key: SYSTEM_LIST_KEYS.coatType,
          limit: 500,
        },
      });
      const rows = response.data?.data || [];
      setCoatTypeOptions(
        rows.map((row) => String(row.value || "").trim()).filter(Boolean),
      );
    } catch (err) {
      console.error("Error fetching Coat Type system list:", err);
      setCoatTypeOptions([]);
    }
  }, []);

  useEffect(() => {
    fetchCoatTypeOptions();
  }, [fetchCoatTypeOptions]);

  const fetchApplicationTypeOptions = useCallback(async () => {
    try {
      const response = await api.get("/system-lists", {
        params: {
          list: APPLICATION_TYPE_LIST_NAME,
          list_key: SYSTEM_LIST_KEYS.applicationType,
          limit: 500,
        },
      });
      const rows = response.data?.data || [];
      setApplicationTypeOptions(
        rows.map((row) => String(row.value || "").trim()).filter(Boolean),
      );
    } catch (err) {
      console.error("Error fetching Application Type system list:", err);
      setApplicationTypeOptions([]);
    }
  }, []);

  useEffect(() => {
    fetchApplicationTypeOptions();
  }, [fetchApplicationTypeOptions]);

  const fetchAccessMethodOptions = useCallback(async () => {
    try {
      const listsResponse = await api.get("/system-lists/lists");
      const listRows = (listsResponse.data?.data || [])
        .map((row) => ({
          list: String(row.list || "").trim(),
          list_id: String(row.list_id || "").trim(),
          list_key: String(row.list_key || "").trim(),
        }))
        .filter((row) => row.list && row.list_id);
      const selectedList = chooseAccessMethodList(listRows);

      if (!selectedList) {
        setAccessMethodListMeta(DEFAULT_ACCESS_METHOD_LIST_META);
        setAccessMethodRows([]);
        return;
      }

      setAccessMethodListMeta({
        listId: selectedList.list_id,
        listName: selectedList.list || ACCESS_METHOD_LIST_NAME,
        listKey: selectedList.list_key || SYSTEM_LIST_KEYS.accessMethods,
      });

      const response = await api.get("/system-lists", {
        params: {
          list_id: selectedList.list_id,
          limit: 500,
        },
      });
      const rows = response.data?.data || [];
      setAccessMethodRows(rows);
    } catch (err) {
      console.error("Error fetching Access Method system list:", err);
      setAccessMethodListMeta(DEFAULT_ACCESS_METHOD_LIST_META);
      setAccessMethodRows([]);
    }
  }, []);

  useEffect(() => {
    fetchAccessMethodOptions();
  }, [fetchAccessMethodOptions]);

  const fetchProductivityMethodAdjustments = useCallback(async () => {
    try {
      setLoadingProductivityMethodAdjustments(true);
      setProductivityMethodAdjustmentError("");
      const response = await api.get(
        "/mobilization-master/productivity-method-adjustments",
      );
      const data = response.data?.data || {
        damageCombinations: [],
        accessMethods: [],
        adjustments: [],
      };
      setProductivityMethodAdjustmentData(data);
      setProductivityMethodAdjustmentValues(
        buildProductivityAdjustmentValueMap(data.adjustments || []),
      );
    } catch (err) {
      console.error("Error fetching productivity method adjustments:", err);
      setProductivityMethodAdjustmentError(
        err.response?.data?.message ||
          "Failed to load productivity adjustments.",
      );
    } finally {
      setLoadingProductivityMethodAdjustments(false);
    }
  }, []);

  useEffect(() => {
    fetchProductivityMethodAdjustments();
  }, [fetchProductivityMethodAdjustments]);

  const fetchExposureClassOptions = useCallback(async () => {
    try {
      const response = await api.get(
        "/corrosion-damage-combinations/options/corrosion-categories",
      );
      const rows = response.data?.data || [];
      setExposureClassOptions(rows);
    } catch (err) {
      console.error("Error fetching exposure class options:", err);
      setExposureClassOptions([]);
    }
  }, []);

  useEffect(() => {
    fetchExposureClassOptions();
  }, [fetchExposureClassOptions]);

  useEffect(() => {
    if (!showGeneralCostModal) return;
    fetchExposureClassOptions();
  }, [fetchExposureClassOptions, showGeneralCostModal]);

  useEffect(() => {
    const fetchSimulationCombinationsAndRules = async () => {
      try {
        const [combinationsRes, rulesRes, processTypesRes, damageSplitsRes] =
          await Promise.all([
            api.get("/corrosion-damage-combinations", {
              params: { limit: 500 },
            }),
            api.get("/repair-rules-new", {
              params: { limit: 1000 },
            }),
            api.get("/repair-process-types"),
            api.get("/damage-splits"),
          ]);

        setSimulationCombinations(combinationsRes.data?.data || []);
        setSimulationRepairRules(rulesRes.data?.data || []);
        setSimulationRepairProcessTypes(processTypesRes.data?.data || []);
        setSimulationDamageSplits(damageSplitsRes.data?.data || []);
      } catch (err) {
        console.error(
          "Error fetching simulation combinations/repair rules:",
          err,
        );
        setSimulationCombinations([]);
        setSimulationRepairRules([]);
        setSimulationRepairProcessTypes([]);
        setSimulationDamageSplits([]);
      }
    };

    fetchSimulationCombinationsAndRules();
  }, []);

  useEffect(() => {
    const fetchMissingConversionRates = async () => {
      const pairs = new Set();

      (generalCostYears || []).forEach((group) => {
        (group.costs || []).forEach((row) => {
          const year = Number(row.year);
          const currency = String(row.currency || defaultSystemCurrency);
          if (currency && currency !== defaultSystemCurrency) {
            pairs.add(`${year}|${currency}|${defaultSystemCurrency}`);
          }
        });
      });

      if (showGeneralCostModal) {
        const year = Number(generalCostForm.year || currentYear);
        const currency = String(
          generalCostForm.currency || defaultSystemCurrency,
        );
        if (currency && currency !== defaultSystemCurrency) {
          pairs.add(`${year}|${currency}|${defaultSystemCurrency}`);
        }
      }

      const missing = Array.from(pairs).filter(
        (key) => generalCostConversionRates[key] === undefined,
      );
      if (missing.length === 0) return;

      const updates = {};
      await Promise.all(
        missing.map(async (key) => {
          const [year, fromCurrency, toCurrency] = key.split("|");
          try {
            const response = await api.get(
              `/currency-conversion-rates/year/${year}/${fromCurrency}/${toCurrency}`,
            );
            const rate = Number(
              response.data?.data?.conversionRate ||
                response.data?.data?.rate ||
                0,
            );
            updates[key] = rate > 0 ? rate : null;
          } catch (err) {
            console.error("Error fetching general cost conversion rate:", err);
            updates[key] = null;
          }
        }),
      );

      if (Object.keys(updates).length > 0) {
        setGeneralCostConversionRates((prev) => ({ ...prev, ...updates }));
      }
    };

    fetchMissingConversionRates();
  }, [
    currentYear,
    defaultSystemCurrency,
    generalCostConversionRates,
    generalCostForm.currency,
    generalCostForm.year,
    generalCostYears,
    showGeneralCostModal,
  ]);

  const mergedCoatTypeOptions = useMemo(() => {
    const merged = [];
    const seen = new Set();

    (coatTypeOptions || [])
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .forEach((value) => {
        const key = value.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          merged.push(value);
        }
      });

    return merged;
  }, [coatTypeOptions]);

  const mergedApplicationTypeOptions = useMemo(() => {
    const seen = new Set();
    const merged = [];

    (applicationTypeOptions || [])
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .forEach((value) => {
        const key = normalizeMethodKey(value);
        if (!seen.has(key)) {
          seen.add(key);
          merged.push(formatMethodLabel(value));
        }
      });

    if (merged.length === 0) {
      return APPLICATION_TYPES;
    }

    return merged;
  }, [applicationTypeOptions]);

  const productivityApplicationTypeDescriptors = useMemo(
    () =>
      mergedApplicationTypeOptions
        .map((label) => {
          const key = normalizeMethodKey(label);
          if (!key) return null;
          return { key, label: formatMethodLabel(label) };
        })
        .filter(Boolean),
    [mergedApplicationTypeOptions],
  );

  const productivityApplicationTypeKeys = useMemo(
    () => productivityApplicationTypeDescriptors.map((item) => item.key),
    [productivityApplicationTypeDescriptors],
  );

  const productivityApplicationTypeLabelByKey = useMemo(() => {
    const map = new Map();
    productivityApplicationTypeDescriptors.forEach((item) => {
      map.set(item.key, item.label);
    });
    return map;
  }, [productivityApplicationTypeDescriptors]);

  const productivityApplicationTypeHeaderLabel = useMemo(() => {
    const labels = productivityApplicationTypeDescriptors.map(
      (item) => item.label,
    );
    if (labels.length === 0) return "Application Types";
    return labels.join(" / ");
  }, [productivityApplicationTypeDescriptors]);

  const sortedAccessMethodRows = useMemo(
    () =>
      [...(accessMethodRows || [])].sort((left, right) => {
        const leftSort = Number(left.sortOrder ?? left.sort_order ?? 100);
        const rightSort = Number(right.sortOrder ?? right.sort_order ?? 100);
        if (leftSort !== rightSort) return leftSort - rightSort;
        return String(left.value || "").localeCompare(
          String(right.value || ""),
        );
      }),
    [accessMethodRows],
  );

  const productivityMethodAdjustmentRows = useMemo(
    () =>
      (productivityMethodAdjustmentData.adjustments || []).map((row) => {
        const key = getAdjustmentMatrixKey(row);
        const savedPercentage = String(
          normalizeProductivityAdjustmentPercent(row.productivityPercentage) ??
            100,
        );
        return {
          ...row,
          adjustmentKey: key,
          savedPercentage,
          productivityPercentage:
            productivityMethodAdjustmentValues[key] ?? savedPercentage,
        };
      }),
    [productivityMethodAdjustmentData.adjustments, productivityMethodAdjustmentValues],
  );

  useEffect(() => {
    if (selectedProductivityMethodFilter === "all") return;
    const selectedStillExists = sortedAccessMethodRows.some(
      (method) => String(method.id) === String(selectedProductivityMethodFilter),
    );
    if (!selectedStillExists) {
      setSelectedProductivityMethodFilter("all");
    }
  }, [selectedProductivityMethodFilter, sortedAccessMethodRows]);

  const visibleProductivityMethodAdjustmentRows = useMemo(() => {
    if (selectedProductivityMethodFilter === "all") {
      return productivityMethodAdjustmentRows;
    }
    return productivityMethodAdjustmentRows.filter(
      (row) =>
        String(row.accessMethodSystemListId) ===
        String(selectedProductivityMethodFilter),
    );
  }, [productivityMethodAdjustmentRows, selectedProductivityMethodFilter]);

  const productivityMethodAdjustmentCountByMethodId = useMemo(() => {
    const counts = new Map();
    productivityMethodAdjustmentRows.forEach((row) => {
      const methodId = String(row.accessMethodSystemListId || "");
      if (!methodId) return;
      counts.set(methodId, (counts.get(methodId) || 0) + 1);
    });
    return counts;
  }, [productivityMethodAdjustmentRows]);

  // Compact matrix view of the adjustment rows: one block per application
  // method, one row per exposure class (C2, C3, ...), one column per damage
  // severity (A, B, C). Shared with the Access Methods modal panel.
  const productivityAdjustmentGrid = useMemo(() => {
    const methodSortIndex = new Map(
      sortedAccessMethodRows.map((method, index) => [
        String(method.id),
        index,
      ]),
    );
    return buildAdjustmentGrid(
      visibleProductivityMethodAdjustmentRows,
      methodSortIndex,
    );
  }, [visibleProductivityMethodAdjustmentRows, sortedAccessMethodRows]);

  const selectedProductivityMethodFilterLabel = useMemo(() => {
    if (selectedProductivityMethodFilter === "all") return "All methods";
    const selectedMethod = sortedAccessMethodRows.find(
      (method) => String(method.id) === String(selectedProductivityMethodFilter),
    );
    return selectedMethod?.value || "Selected method";
  }, [selectedProductivityMethodFilter, sortedAccessMethodRows]);

  const refreshAccessMethodDrivenData = useCallback(async () => {
    await Promise.all([
      fetchAccessMethodOptions(),
      fetchProductivityMethodAdjustments(),
    ]);
  }, [fetchAccessMethodOptions, fetchProductivityMethodAdjustments]);

  useEffect(() => {
    if (!showProductivityModal) return;
    setProductivityForm((prev) => {
      const currentRows = prev.speed_rows || {};
      const normalizedRows = SPEED_BANDS.reduce((acc, speed) => {
        const base = createEmptySpeedSqftRows(
          productivityApplicationTypeKeys,
        )[speed];
        const existing = currentRows[speed] || {};
        acc[speed] = {
          ...base,
          ...existing,
        };
        return acc;
      }, {});

      return {
        ...prev,
        speed_rows: normalizedRows,
      };
    });
  }, [productivityApplicationTypeKeys, showProductivityModal]);

  const handleAddCoatTypeOption = async (newValue) => {
    try {
      await api.post("/system-lists", {
        list: "Coat Type",
        value: newValue,
        is_active: true,
      });
      await fetchCoatTypeOptions();
      return true;
    } catch (err) {
      alert(err.response?.data?.message || "Failed to add Coat Type option.");
      return false;
    }
  };

  const openCreateAccessMethodModal = () => {
    setEditingAccessMethod(null);
    setAccessMethodForm({ value: "", sort_order: "100" });
    setAccessMethodError("");
    setShowAccessMethodModal(true);
  };

  const openEditAccessMethodModal = (row) => {
    setEditingAccessMethod(row);
    setAccessMethodForm({
      value: String(row.value || ""),
      sort_order: String(row.sortOrder ?? row.sort_order ?? 100),
    });
    setAccessMethodError("");
    setShowAccessMethodModal(true);
  };

  const closeAccessMethodModal = () => {
    setShowAccessMethodModal(false);
    setEditingAccessMethod(null);
    setAccessMethodForm({ value: "", sort_order: "100" });
    setAccessMethodError("");
  };

  const handleSaveAccessMethod = async () => {
    const value = String(accessMethodForm.value || "").trim();
    const sortOrder = parseInt(accessMethodForm.sort_order, 10);

    if (!value) {
      setAccessMethodError("Method name is required.");
      return;
    }
    if (!Number.isInteger(sortOrder)) {
      setAccessMethodError("Sort order must be a whole number.");
      return;
    }

    const listId =
      editingAccessMethod?.listId ||
      editingAccessMethod?.list_id ||
      accessMethodListMeta.listId;
    const listName =
      editingAccessMethod?.list ||
      accessMethodListMeta.listName ||
      ACCESS_METHOD_LIST_NAME;

    try {
      setSavingAccessMethod(true);
      setAccessMethodError("");

      const payload = {
        ...(listId ? { list_id: listId } : { list: listName }),
        list: listName,
        list_key: SYSTEM_LIST_KEYS.accessMethods,
        value,
        sort_order: sortOrder,
        is_active: true,
      };

      if (editingAccessMethod?.id) {
        await api.put(`/system-lists/${editingAccessMethod.id}`, payload);
      } else {
        await api.post("/system-lists", payload);
      }

      closeAccessMethodModal();
      await refreshAccessMethodDrivenData();
    } catch (err) {
      setAccessMethodError(
        err.response?.data?.message || "Failed to save access method.",
      );
    } finally {
      setSavingAccessMethod(false);
    }
  };

  const handleDeleteAccessMethod = async (row) => {
    if (!window.confirm(`Delete access method '${row.value}'?`)) return;
    try {
      const res = await api.delete(`/system-lists/${row.id}`);
      const updated =
        res.data?.data?.accessMethodCleanup?.updatedBridgeElements || 0;
      await refreshAccessMethodDrivenData();
      if (updated > 0) {
        alert(
          `Deleted '${row.value}'. Removed its reference from ${updated} bridge element(s) and re-normalized their remaining methods.`,
        );
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete access method.");
    }
  };

  const [pruningOrphanMethods, setPruningOrphanMethods] = useState(false);

  const handlePruneOrphanMethods = async () => {
    if (
      !window.confirm(
        "Scan all bridges and remove any access-method references that no longer exist in this list (re-normalizing the remaining splits)?",
      )
    ) {
      return;
    }
    try {
      setPruningOrphanMethods(true);
      const res = await api.post(
        "/bridge-structure/access-methods/prune-orphans",
      );
      const { scanned = 0, updated = 0 } = res.data?.data || {};
      alert(
        updated > 0
          ? `Cleaned ${updated} of ${scanned} bridge element(s) with method assignments.`
          : `No stale references found (${scanned} element(s) scanned).`,
      );
    } catch (err) {
      alert(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to clean up bridge method references.",
      );
    } finally {
      setPruningOrphanMethods(false);
    }
  };

  const updateProductivityMethodAdjustmentPercent = (row, value) => {
    // A keystroke that would produce a leading zero or exceed the ceiling never
    // lands; the field keeps whatever it already held.
    if (!isAllowedAdjustmentText(value)) return;
    const nextValue = value === "" ? "" : String(value);
    const key = getAdjustmentMatrixKey(row);
    if (isValidAdjustmentPercent(nextValue)) {
      lastValidProductivityAdjustmentRef.current[key] = nextValue;
    }
    setProductivityMethodAdjustmentValues((prev) => ({
      ...prev,
      [key]: nextValue,
    }));
  };

  // Leaving a cell always commits an in-range value: round a valid entry to two
  // decimals, or restore the last in-range one if the field was left empty or
  // at zero. Zero is never substituted for a minimum — the previous value
  // simply comes back.
  const commitProductivityMethodAdjustmentPercent = (row) => {
    const key = getAdjustmentMatrixKey(row);
    const current = productivityMethodAdjustmentValues[key];
    if (current === undefined) return;

    const normalized = normalizeProductivityAdjustmentPercent(current);
    const nextValue =
      normalized !== null
        ? String(normalized)
        : lastValidProductivityAdjustmentRef.current[key] ??
          row.savedPercentage ??
          "100";

    lastValidProductivityAdjustmentRef.current[key] = nextValue;
    if (nextValue !== String(current)) {
      setProductivityMethodAdjustmentValues((prev) => ({
        ...prev,
        [key]: nextValue,
      }));
    }
  };

  const handleSaveProductivityMethodAdjustments = async () => {
    const invalidRows = productivityMethodAdjustmentRows.filter(
      (row) =>
        normalizeProductivityAdjustmentPercent(row.productivityPercentage) ===
        null,
    );

    if (invalidRows.length > 0) {
      setProductivityMethodAdjustmentError(
        `Productivity percentages must be greater than 0 and at most 100. Check: ${invalidRows
          .map(
            (row) => `${row.combinationCode} - ${row.accessMethodName}`,
          )
          .join(", ")}.`,
      );
      return;
    }

    try {
      setSavingProductivityMethodAdjustments(true);
      setProductivityMethodAdjustmentError("");
      const response = await api.put(
        "/mobilization-master/productivity-method-adjustments",
        {
          adjustments: productivityMethodAdjustmentRows.map((row) => ({
            corrosionDamageCombinationId: row.corrosionDamageCombinationId,
            accessMethodSystemListId: row.accessMethodSystemListId,
            productivityPercentage: normalizeProductivityAdjustmentPercent(
              row.productivityPercentage,
            ),
          })),
        },
      );
      const data = response.data?.data || {
        damageCombinations: [],
        accessMethods: [],
        adjustments: [],
      };
      setProductivityMethodAdjustmentData(data);
      setProductivityMethodAdjustmentValues(
        buildProductivityAdjustmentValueMap(data.adjustments || []),
      );
    } catch (err) {
      setProductivityMethodAdjustmentError(
        err.response?.data?.message ||
          "Failed to save productivity adjustments.",
      );
    } finally {
      setSavingProductivityMethodAdjustments(false);
    }
  };

  const allYears = useMemo(() => {
    const years = new Set();
    jobYears.forEach((g) => years.add(Number(g.year)));
    teamYears.forEach((g) => years.add(Number(g.year)));
    generalCostYears.forEach((g) => years.add(Number(g.year)));
    mobilizationConfigYears.forEach((g) => years.add(Number(g.year)));
    years.add(currentYear);
    return Array.from(years).sort((a, b) => b - a);
  }, [
    currentYear,
    generalCostYears,
    jobYears,
    mobilizationConfigYears,
    teamYears,
  ]);

  const productivityAllYears = useMemo(() => {
    const years = new Set();
    productivityYears.forEach((g) => years.add(Number(g.year)));
    years.add(currentYear);
    return Array.from(years).sort((a, b) => b - a);
  }, [currentYear, productivityYears]);

  const generalCostAllYears = useMemo(() => {
    const years = new Set();
    generalCostYears.forEach((g) => years.add(Number(g.year)));
    years.add(currentYear);
    return Array.from(years).sort((a, b) => b - a);
  }, [currentYear, generalCostYears]);

  const mobilizationConfigAllYears = useMemo(() => {
    const years = new Set();
    mobilizationConfigYears.forEach((g) => years.add(Number(g.year)));
    years.add(currentYear);
    return Array.from(years).sort((a, b) => b - a);
  }, [currentYear, mobilizationConfigYears]);

  const modalYearOptions = useMemo(() => {
    const years = new Set(allYears);
    years.add(currentYear + 1);
    return Array.from(years).sort((a, b) => b - a);
  }, [allYears, currentYear]);

  const jobsByYear = useMemo(() => {
    const map = new Map();
    jobYears.forEach((group) => {
      map.set(Number(group.year), group.jobs || []);
    });
    return map;
  }, [jobYears]);

  const teamsById = useMemo(() => {
    const map = new Map();
    teamYears.forEach((group) => {
      (group.teams || []).forEach((team) => {
        map.set(Number(team.id), team);
      });
    });
    return map;
  }, [teamYears]);

  const resolveTeamDisplayName = (teamLike, fallback = "-") => {
    const teamId = resolveTeamIdFromValue(teamLike);
    if (teamId !== null && teamsById.has(teamId)) {
      return getTeamDisplayName(teamsById.get(teamId)) || fallback;
    }

    return getTeamDisplayName(teamLike) || fallback;
  };

  const resetJobForm = () => {
    setEditingJobId(null);
    setJobForm({
      ...EMPTY_JOB_FORM,
      year: currentYear,
      currency: defaultSystemCurrency,
      converted_rate_per_hour: "",
    });
  };

  const resetTeamForm = () => {
    setEditingTeamId(null);
    setTeamForm({ ...EMPTY_TEAM_FORM, year: currentYear });
  };

  const openCreateJobModal = () => {
    resetJobForm();
    setShowJobModal(true);
  };

  const openEditJobModal = (job, year) => {
    setEditingJobId(job.id);
    setJobForm({
      year: Number(year),
      job_name: String(job.jobName || job.job_name || ""),
      job_identifier: getJobIdentifier(job),
      rate_per_hour: String(toNum(job.ratePerHour || job.rate_per_hour || 0)),
      currency: String(job.currency || defaultSystemCurrency),
      converted_rate_per_hour: String(
        toNum(
          job.convertedRatePerHour ||
            job.converted_rate_per_hour ||
            job.ratePerHour ||
            job.rate_per_hour ||
            0,
        ),
      ),
      hours_per_week: String(
        toNum(job.hoursPerWeek || job.hours_per_week || 0),
      ),
      week_count: String(toNum(job.weekCount || job.week_count || 0)),
      painting_time_pct: String(
        toNum(job.paintingTimePct || job.painting_time_pct || 100),
      ),
      sort_order: String(toInt(job.sortOrder ?? job.sort_order ?? 99)),
    });
    setShowJobModal(true);
  };

  const closeJobModal = () => {
    setShowJobModal(false);
    resetJobForm();
  };

  useEffect(() => {
    const updateConvertedJobRate = async () => {
      if (!showJobModal) return;

      const rate = toNum(jobForm.rate_per_hour);
      if (!jobForm.currency || rate <= 0) {
        setJobForm((prev) => ({ ...prev, converted_rate_per_hour: "" }));
        return;
      }

      if (jobForm.currency === defaultSystemCurrency) {
        setJobForm((prev) => ({
          ...prev,
          converted_rate_per_hour: String(rate.toFixed(2)),
        }));
        return;
      }

      try {
        setConvertingJobRate(true);
        const year = Number(jobForm.year || currentYear);
        const response = await api.get(
          `/currency-conversion-rates/year/${year}/${jobForm.currency}/${defaultSystemCurrency}`,
        );
        const conversionRate = Number(
          response.data?.data?.conversionRate || response.data?.data?.rate || 1,
        );
        const converted = rate * conversionRate;
        setJobForm((prev) => ({
          ...prev,
          converted_rate_per_hour: String(converted.toFixed(2)),
        }));
      } catch (err) {
        console.error("Error converting job rate:", err);
        setJobForm((prev) => ({ ...prev, converted_rate_per_hour: "" }));
      } finally {
        setConvertingJobRate(false);
      }
    };

    updateConvertedJobRate();
  }, [
    showJobModal,
    jobForm.year,
    jobForm.currency,
    jobForm.rate_per_hour,
    currentYear,
    defaultSystemCurrency,
  ]);

  const handleSaveJob = async () => {
    if (!jobForm.job_name.trim()) {
      alert("Job name is required.");
      return;
    }

    const normalizedJobIdentifier = sanitizeJobIdentifierInput(
      jobForm.job_identifier,
    );
    if (!normalizedJobIdentifier) {
      alert("Job identifier is required.");
      return;
    }

    const rawSortOrder = String(jobForm.sort_order ?? "").trim();
    const parsedSortOrder = Number(rawSortOrder);
    if (!rawSortOrder || !Number.isInteger(parsedSortOrder)) {
      alert("Sort order must be an integer.");
      return;
    }

    setSavingJob(true);
    try {
      const payload = {
        year: Number(jobForm.year),
        job_name: jobForm.job_name.trim(),
        job_identifier: normalizedJobIdentifier,
        rate_per_hour: toNum(jobForm.rate_per_hour),
        currency: jobForm.currency || defaultSystemCurrency,
        converted_rate_per_hour: toNum(
          jobForm.converted_rate_per_hour || jobForm.rate_per_hour,
        ),
        hours_per_week: toNum(jobForm.hours_per_week),
        painting_time_pct: toNum(jobForm.painting_time_pct || 100),
        sort_order: parsedSortOrder,
      };

      if (editingJobId) {
        await api.put(
          `/mobilization-master/painter-jobs/${editingJobId}`,
          payload,
        );
      } else {
        await api.post("/mobilization-master/painter-jobs", payload);
      }

      closeJobModal();
      await fetchPaintersData();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to save job.");
    } finally {
      setSavingJob(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm("Delete this job?")) return;
    try {
      await api.delete(`/mobilization-master/painter-jobs/${jobId}`);
      await fetchPaintersData();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete job.");
    }
  };

  const toggleJobYear = (year) => {
    setExpandedJobYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) next.delete(year);
      else next.add(year);
      return next;
    });
  };

  const toggleTeamYear = (year) => {
    setExpandedTeamYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) next.delete(year);
      else next.add(year);
      return next;
    });
  };

  const handleCopyJobsFromYear = (sourceYear) => {
    const source = jobYears.find((g) => Number(g.year) === Number(sourceYear));
    if (!source || !(source.jobs || []).length) {
      alert(`No jobs found in source year ${sourceYear}.`);
      return;
    }

    const usedYears = new Set(allYears.map((y) => Number(y)));
    let suggestedYear = Number(sourceYear) + 1;
    while (usedYears.has(suggestedYear) && suggestedYear <= 3000) {
      suggestedYear += 1;
    }

    setCopyJobsSourceYear(Number(sourceYear));
    setCopyJobsTargetYear(String(suggestedYear));
    setCopyJobsError("");
    setShowCopyJobModal(true);
  };

  const handleConfirmCopyJobs = async () => {
    if (!copyJobsSourceYear) return;

    const targetYear = parseInt(copyJobsTargetYear, 10);
    if (
      !Number.isInteger(targetYear) ||
      targetYear < 1900 ||
      targetYear > 3000
    ) {
      setCopyJobsError("Please enter a valid target year.");
      return;
    }
    if (targetYear === copyJobsSourceYear) {
      setCopyJobsError("Target year must be different from source year.");
      return;
    }
    if (allYears.includes(targetYear)) {
      setCopyJobsError(
        `Year ${targetYear} already exists. Please choose a new year.`,
      );
      return;
    }

    const source = jobYears.find(
      (g) => Number(g.year) === Number(copyJobsSourceYear),
    );
    if (!source || !(source.jobs || []).length) {
      setCopyJobsError(`No jobs found in source year ${copyJobsSourceYear}.`);
      return;
    }

    try {
      setCopyingJobs(true);
      setCopyJobsError("");
      const requests = (source.jobs || []).map((job, index) =>
        api.post("/mobilization-master/painter-jobs", {
          year: targetYear,
          job_name: job.jobName || job.job_name,
          job_identifier:
            getJobIdentifier(job) ||
            getFallbackJobIdentifierFromName(job.jobName || job.job_name),
          rate_per_hour: toNum(job.ratePerHour || job.rate_per_hour),
          hours_per_week: toNum(job.hoursPerWeek || job.hours_per_week),
          week_count: toNum(job.weekCount || job.week_count),
          painting_time_pct: toNum(
            job.paintingTimePct || job.painting_time_pct || 100,
          ),
          sort_order: toInt(job.sortOrder ?? job.sort_order ?? index + 1),
        }),
      );
      const results = await Promise.allSettled(requests);
      const successful = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - successful;

      if (failed > 0) {
        alert(
          `Copied ${successful} of ${results.length} jobs. ${failed} failed.`,
        );
      } else {
        alert(
          `Successfully copied ${successful} jobs from ${copyJobsSourceYear} to ${targetYear}.`,
        );
      }

      setShowCopyJobModal(false);
      setCopyJobsSourceYear(null);
      setCopyJobsTargetYear("");
      setCopyJobsError("");
      await fetchPaintersData();
    } catch (err) {
      setCopyJobsError(err.response?.data?.message || "Failed to copy jobs.");
    } finally {
      setCopyingJobs(false);
    }
  };

  const handleCopyTeamsFromYear = (sourceYear) => {
    const source = teamYears.find((g) => Number(g.year) === Number(sourceYear));
    if (!source || !(source.teams || []).length) {
      alert(`No teams found in source year ${sourceYear}.`);
      return;
    }

    const usedYears = new Set((teamYears || []).map((g) => Number(g.year)));
    let suggestedYear = Number(sourceYear) + 1;
    while (usedYears.has(suggestedYear) && suggestedYear <= 3000) {
      suggestedYear += 1;
    }

    setCopyTeamsSourceYear(Number(sourceYear));
    setCopyTeamsTargetYear(String(suggestedYear));
    setCopyTeamsError("");
    setShowCopyTeamModal(true);
  };

  const handleConfirmCopyTeams = async () => {
    if (!copyTeamsSourceYear) return;

    const targetYear = parseInt(copyTeamsTargetYear, 10);
    if (
      !Number.isInteger(targetYear) ||
      targetYear < 1900 ||
      targetYear > 3000
    ) {
      setCopyTeamsError("Please enter a valid target year.");
      return;
    }
    if (targetYear === copyTeamsSourceYear) {
      setCopyTeamsError("Target year must be different from source year.");
      return;
    }

    const hasTargetYear = (teamYears || []).some(
      (g) => Number(g.year) === Number(targetYear),
    );
    if (hasTargetYear) {
      setCopyTeamsError(
        `Year ${targetYear} already has team configurations. Please choose a new year.`,
      );
      return;
    }

    const source = teamYears.find(
      (g) => Number(g.year) === Number(copyTeamsSourceYear),
    );
    if (!source || !(source.teams || []).length) {
      setCopyTeamsError(
        `No teams found in source year ${copyTeamsSourceYear}.`,
      );
      return;
    }

    const targetJobs = jobsByYear.get(Number(targetYear)) || [];
    if (!targetJobs.length) {
      setCopyTeamsError(
        `No jobs found in target year ${targetYear}. Copy jobs first, then copy teams.`,
      );
      return;
    }

    const normalizeName = (value) =>
      String(value || "")
        .trim()
        .toLowerCase();
    const targetJobIdByName = new Map(
      targetJobs.map((job) => [
        normalizeName(job.jobName || job.job_name),
        Number(job.id),
      ]),
    );

    const missingJobNames = new Set();
    const payloads = [];

    (source.teams || []).forEach((team) => {
      const sourceRows = team.jobAllocations || team.job_allocations || [];
      const allocations = sourceRows
        .map((row) => {
          const sourceJobName = row.job?.jobName || row.job?.job_name || "";
          const mappedJobId = targetJobIdByName.get(
            normalizeName(sourceJobName),
          );
          if (!mappedJobId) {
            if (sourceJobName) missingJobNames.add(sourceJobName);
            return null;
          }
          return {
            job_id: Number(mappedJobId),
            member_count: toInt(row.memberCount || row.member_count),
          };
        })
        .filter((row) => row && row.member_count > 0);

      if (allocations.length > 0) {
        payloads.push({
          year: Number(targetYear),
          sort_order: Number(team.sortOrder ?? team.sort_order ?? 99),
          allocations,
        });
      }
    });

    if (missingJobNames.size > 0) {
      setCopyTeamsError(
        `Missing matching jobs in ${targetYear}: ${Array.from(missingJobNames).join(", ")}.`,
      );
      return;
    }

    if (!payloads.length) {
      setCopyTeamsError("No valid team rows could be copied.");
      return;
    }

    try {
      setCopyingTeams(true);
      setCopyTeamsError("");

      const results = await Promise.allSettled(
        payloads.map((payload) =>
          api.post("/mobilization-master/painter-team-configs", payload),
        ),
      );
      const successful = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - successful;

      if (failed > 0) {
        alert(
          `Copied ${successful} of ${results.length} teams. ${failed} failed.`,
        );
      } else {
        alert(
          `Successfully copied ${successful} teams from ${copyTeamsSourceYear} to ${targetYear}.`,
        );
      }

      setShowCopyTeamModal(false);
      setCopyTeamsSourceYear(null);
      setCopyTeamsTargetYear("");
      setCopyTeamsError("");
      await fetchPaintersData();
    } catch (err) {
      setCopyTeamsError(
        err.response?.data?.message || "Failed to copy team configurations.",
      );
    } finally {
      setCopyingTeams(false);
    }
  };

  const openCreateTeamModal = () => {
    resetTeamForm();
    setShowTeamModal(true);
  };

  const openEditTeamModal = (team, year) => {
    const allocations = sortTeamAllocationRows(
      team.jobAllocations || team.job_allocations || [],
    ).map((alloc) => ({
      job_id: String(alloc.jobId || alloc.job_id || ""),
      member_count: String(toInt(alloc.memberCount || alloc.member_count || 0)),
    }));

    setEditingTeamId(team.id);
    setTeamForm({
      year: Number(year),
      sort_order: String(team.sortOrder ?? team.sort_order ?? 99),
      allocations:
        allocations.length > 0 ? allocations : [{ ...EMPTY_ALLOCATION }],
    });
    setShowTeamModal(true);
  };

  const closeTeamModal = () => {
    setShowTeamModal(false);
    resetTeamForm();
  };

  const updateAllocationField = (index, field, value) => {
    setTeamForm((prev) => {
      const next = [...prev.allocations];
      next[index] = { ...next[index], [field]: value };
      return {
        ...prev,
        allocations: sortTeamAllocationRows(next, jobsForSelectedTeamYearById),
      };
    });
  };

  const addAllocationRow = () => {
    setTeamForm((prev) => ({
      ...prev,
      allocations: sortTeamAllocationRows(
        [...prev.allocations, { ...EMPTY_ALLOCATION }],
        jobsForSelectedTeamYearById,
      ),
    }));
  };

  const removeAllocationRow = (index) => {
    setTeamForm((prev) => {
      if (prev.allocations.length <= 1) return prev;
      return {
        ...prev,
        allocations: sortTeamAllocationRows(
          prev.allocations.filter((_, i) => i !== index),
          jobsForSelectedTeamYearById,
        ),
      };
    });
  };

  const jobsForSelectedTeamYear = useMemo(() => {
    return jobsByYear.get(Number(teamForm.year)) || [];
  }, [jobsByYear, teamForm.year]);

  const jobsForSelectedTeamYearById = useMemo(
    () =>
      new Map(jobsForSelectedTeamYear.map((job) => [String(job.id), job])),
    [jobsForSelectedTeamYear],
  );

  const generatedTeamName = useMemo(
    () => buildGeneratedTeamName(teamForm.allocations, jobsForSelectedTeamYearById),
    [teamForm.allocations, jobsForSelectedTeamYearById],
  );

  useEffect(() => {
    setTeamForm((prev) => {
      const validJobIds = new Set(jobsForSelectedTeamYearById.keys());
      const updated = sortTeamAllocationRows(
        prev.allocations.map((row) => ({
          ...row,
          job_id: validJobIds.has(String(row.job_id)) ? row.job_id : "",
        })),
        jobsForSelectedTeamYearById,
      );

      return areAllocationRowsEqual(prev.allocations, updated)
        ? prev
        : { ...prev, allocations: updated };
    });
  }, [jobsForSelectedTeamYearById]);

  const handleSaveTeam = async () => {
    const rawSortOrder = String(teamForm.sort_order ?? "").trim();
    const parsedSortOrder = Number(rawSortOrder);
    if (!rawSortOrder || !Number.isInteger(parsedSortOrder)) {
      alert("Sort order must be an integer.");
      return;
    }

    const sortedValidAllocations = sortTeamAllocationRows(
      teamForm.allocations,
      jobsForSelectedTeamYearById,
    ).filter((row) => {
      const job = getAllocationJob(row, jobsForSelectedTeamYearById);
      return job && toInt(row.member_count) > 0;
    });

    if (sortedValidAllocations.length === 0) {
      alert(
        "Add at least one job allocation with a valid job and member count.",
      );
      return;
    }

    if (!generatedTeamName) {
      alert("Team name could not be generated from the selected job rows.");
      return;
    }

    setSavingTeam(true);
    try {
      const payload = {
        year: Number(teamForm.year),
        team_name: generatedTeamName,
        sort_order: parsedSortOrder,
        allocations: sortedValidAllocations.map((row) => {
          const job = getAllocationJob(row, jobsForSelectedTeamYearById);
          return {
            job_id: Number(row.job_id),
            member_count: toInt(row.member_count),
            sort_order: getJobSortOrder(job),
          };
        }),
      };

      if (editingTeamId) {
        await api.put(
          `/mobilization-master/painter-team-configs/${editingTeamId}`,
          payload,
        );
      } else {
        await api.post("/mobilization-master/painter-team-configs", payload);
      }

      closeTeamModal();
      await fetchPaintersData();
    } catch (err) {
      alert(
        err.response?.data?.message || "Failed to save team configuration.",
      );
    } finally {
      setSavingTeam(false);
    }
  };

  const handleDeleteTeamConfig = async (teamId) => {
    if (!window.confirm("Delete this team configuration?")) return;
    try {
      await api.delete(`/mobilization-master/painter-team-configs/${teamId}`);
      await fetchPaintersData();
    } catch (err) {
      alert(
        err.response?.data?.message || "Failed to delete team configuration.",
      );
    }
  };

  const productivityMatrixByYear = useMemo(() => {
    const matrix = [];
    const validApplicationTypeKeys = new Set(productivityApplicationTypeKeys);
    (productivityYears || []).forEach((group) => {
      const map = new Map();
      (group.standards || []).forEach((row) => {
        const steel = row.steelSize || row.steel_size || "";
        const coat = row.coatType || row.coat_type || "";
        const speed = row.speedBand || row.speed_band || "";
        const method = normalizeMethodKey(
          row.accessMethod || row.access_method || "",
        );
        if (!method || !validApplicationTypeKeys.has(method)) return;
        const key = `${group.year}|${steel}|${coat}|${speed}`;

        if (!map.has(key)) {
          map.set(key, {
            key,
            year: Number(group.year),
            steel_size: steel,
            coat_type: coat,
            speed_band: speed,
            ids: {},
            sqft: {},
          });
        }

        const item = map.get(key);
        item.ids[method] = row.id;
        item.sqft[method] = toNum(row.sqftPerHour || row.sqft_per_hour);
      });

      matrix.push(...Array.from(map.values()));
    });

    return matrix.sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      if (a.steel_size !== b.steel_size)
        return a.steel_size.localeCompare(b.steel_size);
      if (a.coat_type !== b.coat_type)
        return a.coat_type.localeCompare(b.coat_type);
      return a.speed_band.localeCompare(b.speed_band);
    });
  }, [productivityApplicationTypeKeys, productivityYears]);

  const productivityMatrixGrouped = useMemo(() => {
    const grouped = {};
    productivityMatrixByYear.forEach((row) => {
      if (!grouped[row.year]) grouped[row.year] = [];
      grouped[row.year].push(row);
    });
    return Object.entries(grouped)
      .map(([year, rows]) => ({ year: Number(year), rows }))
      .sort((a, b) => b.year - a.year);
  }, [productivityMatrixByYear]);

  const toggleProductivityYear = (year) => {
    setExpandedProductivityYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) next.delete(year);
      else next.add(year);
      return next;
    });
  };

  const resetProductivityForm = () => {
    setEditingProductivityKey(null);
    setProductivityForm({
      year: currentYear,
      steel_size: "HSS",
      coat_type: "Prime",
      speed_rows: createEmptySpeedSqftRows(productivityApplicationTypeKeys),
    });
  };

  const openCreateProductivityModal = () => {
    resetProductivityForm();
    setShowProductivityModal(true);
  };

  const openEditProductivityModal = (row) => {
    setEditingProductivityKey(row.key);
    const speedRows = createEmptySpeedSqftRows(productivityApplicationTypeKeys);
    productivityMatrixByYear
      .filter(
        (item) =>
          Number(item.year) === Number(row.year) &&
          item.steel_size === row.steel_size &&
          item.coat_type === row.coat_type,
      )
      .forEach((item) => {
        const speed = item.speed_band;
        if (!SPEED_BANDS.includes(speed)) return;
        const nextBand = {
          ...(speedRows[speed] || {}),
        };
        Object.entries(item.sqft || {}).forEach(([methodKey, value]) => {
          nextBand[methodKey] = String(toNum(value));
        });
        speedRows[speed] = nextBand;
      });

    setProductivityForm({
      year: row.year,
      steel_size: row.steel_size,
      coat_type: row.coat_type,
      speed_rows: speedRows,
    });
    setShowProductivityModal(true);
  };

  const closeProductivityModal = () => {
    setShowProductivityModal(false);
    resetProductivityForm();
  };

  const handleSaveProductivity = async () => {
    const speedRows =
      productivityForm.speed_rows ||
      createEmptySpeedSqftRows(productivityApplicationTypeKeys);
    const hasAnyValue = SPEED_BANDS.some((speed) => {
      const band = speedRows[speed] || {};
      return Object.values(band).some((value) => toNum(value) > 0);
    });

    if (!hasAnyValue) {
      alert("Enter at least one positive sqft/hour value.");
      return;
    }

    setSavingProductivity(true);
    try {
      const existingRowsBySpeed = new Map();
      if (editingProductivityKey) {
        productivityMatrixByYear
          .filter(
            (item) =>
              Number(item.year) === Number(productivityForm.year) &&
              item.steel_size === productivityForm.steel_size &&
              item.coat_type === productivityForm.coat_type,
          )
          .forEach((item) => existingRowsBySpeed.set(item.speed_band, item));
      }

      const requests = [];
      SPEED_BANDS.forEach((speed) => {
        const band = speedRows[speed] || {};
        const existing = existingRowsBySpeed.get(speed);
        const methodKeysForBand = Array.from(
          new Set(productivityApplicationTypeKeys),
        ).filter(Boolean);

        const payloadRows = methodKeysForBand.map((methodKey) => ({
          methodKey,
          method:
            productivityApplicationTypeLabelByKey.get(methodKey) ||
            formatMethodLabel(methodKey),
          sqft: toNum(band[methodKey]),
        }));

        payloadRows.forEach((row) => {
          const existingId = existing?.ids?.[row.methodKey];

          if (row.sqft <= 0) {
            if (existingId) {
              requests.push(
                api.delete(`/mobilization-master/productivity/${existingId}`),
              );
            }
            return;
          }

          const sqm = row.sqft * SQFT_TO_SQM;
          const manhours = sqm > 0 ? 1 / sqm : 0;
          const payload = {
            year: Number(productivityForm.year),
            steel_size: productivityForm.steel_size,
            coat_type: productivityForm.coat_type,
            speed_band: speed,
            access_method: row.method,
            sqft_per_hour: row.sqft,
            sqm_per_hour: sqm,
            manhours_per_sqm: manhours,
          };

          if (existingId) {
            requests.push(
              api.put(
                `/mobilization-master/productivity/${existingId}`,
                payload,
              ),
            );
          } else {
            requests.push(
              api.post("/mobilization-master/productivity", payload),
            );
          }
        });
      });

      await Promise.all(requests);
      closeProductivityModal();
      await fetchPaintersData();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to save productivity row.");
    } finally {
      setSavingProductivity(false);
    }
  };

  const handleDeleteProductivity = async (row) => {
    if (!window.confirm("Delete this productivity row?")) return;
    const ids = Object.values(row.ids || {}).filter(Boolean);
    if (ids.length === 0) return;

    try {
      await Promise.all(
        ids.map((id) => api.delete(`/mobilization-master/productivity/${id}`)),
      );
      await fetchPaintersData();
    } catch (err) {
      alert(
        err.response?.data?.message || "Failed to delete productivity row.",
      );
    }
  };

  const handleCopyProductivityFromYear = (sourceYear) => {
    const source = productivityMatrixGrouped.find(
      (g) => Number(g.year) === Number(sourceYear),
    );
    if (!source || !(source.rows || []).length) {
      alert(`No productivity rows found in source year ${sourceYear}.`);
      return;
    }

    const usedYears = new Set(productivityAllYears.map((y) => Number(y)));
    let suggestedYear = Number(sourceYear) + 1;
    while (usedYears.has(suggestedYear) && suggestedYear <= 3000) {
      suggestedYear += 1;
    }

    setCopyProductivitySourceYear(Number(sourceYear));
    setCopyProductivityTargetYear(String(suggestedYear));
    setCopyProductivityError("");
    setShowCopyProductivityModal(true);
  };

  const handleConfirmCopyProductivity = async () => {
    if (!copyProductivitySourceYear) return;

    const targetYear = parseInt(copyProductivityTargetYear, 10);
    if (
      !Number.isInteger(targetYear) ||
      targetYear < 1900 ||
      targetYear > 3000
    ) {
      setCopyProductivityError("Please enter a valid target year.");
      return;
    }
    if (targetYear === copyProductivitySourceYear) {
      setCopyProductivityError(
        "Target year must be different from source year.",
      );
      return;
    }
    if (productivityAllYears.includes(targetYear)) {
      setCopyProductivityError(
        `Year ${targetYear} already exists. Please choose a new year.`,
      );
      return;
    }

    const source = productivityMatrixGrouped.find(
      (g) => Number(g.year) === Number(copyProductivitySourceYear),
    );
    if (!source || !(source.rows || []).length) {
      setCopyProductivityError(
        `No productivity rows found in source year ${copyProductivitySourceYear}.`,
      );
      return;
    }

    try {
      setCopyingProductivity(true);
      setCopyProductivityError("");

      const requests = [];
      (source.rows || []).forEach((row) => {
        Object.entries(row.sqft || {}).forEach(([methodKey]) => {
          const sqft = toNum(row.sqft?.[methodKey]);
          if (sqft <= 0) return;

          const sqm = sqft * SQFT_TO_SQM;
          const manhours = sqm > 0 ? 1 / sqm : 0;
          requests.push(
            api.post("/mobilization-master/productivity", {
              year: targetYear,
              steel_size: row.steel_size,
              coat_type: row.coat_type,
              speed_band: row.speed_band,
              access_method:
                productivityApplicationTypeLabelByKey.get(methodKey) ||
                formatMethodLabel(methodKey),
              sqft_per_hour: sqft,
              sqm_per_hour: sqm,
              manhours_per_sqm: manhours,
            }),
          );
        });
      });

      const results = await Promise.allSettled(requests);
      const successful = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - successful;

      if (failed > 0) {
        alert(
          `Copied ${successful} of ${results.length} productivity rows. ${failed} failed.`,
        );
      } else {
        alert(
          `Successfully copied ${successful} productivity rows from ${copyProductivitySourceYear} to ${targetYear}.`,
        );
      }

      setShowCopyProductivityModal(false);
      setCopyProductivitySourceYear(null);
      setCopyProductivityTargetYear("");
      setCopyProductivityError("");
      await fetchPaintersData();
    } catch (err) {
      setCopyProductivityError(
        err.response?.data?.message || "Failed to copy productivity rows.",
      );
    } finally {
      setCopyingProductivity(false);
    }
  };

  const exposureCodeById = useMemo(() => {
    const map = new Map();
    (exposureClassOptions || []).forEach((row) => {
      map.set(Number(row.id), row.categoryCode || row.category_code);
    });
    return map;
  }, [exposureClassOptions]);

  const normalizeExposureIds = (ids) => {
    if (!Array.isArray(ids)) return [];
    return ids
      .map((id) => parseInt(id, 10))
      .filter((id) => Number.isInteger(id) && id > 0);
  };

  const getGeneralCostConversionRate = useCallback(
    (data) => {
      const year = Number(data.year || currentYear);
      const currency = String(data.currency || defaultSystemCurrency);
      if (!currency || currency === defaultSystemCurrency) return 1;
      const key = `${year}|${currency}|${defaultSystemCurrency}`;
      const rate = generalCostConversionRates[key];
      return Number(rate || 0) > 0 ? Number(rate) : null;
    },
    [currentYear, defaultSystemCurrency, generalCostConversionRates],
  );

  const calculateGeneralCostTotal = (data, useSystemCurrency = true) => {
    const total = calculateGeneralCostBaseTotal({
      data,
      weekCountForPreview: toNum(data.week_count || data.weekCount),
    });

    if (!useSystemCurrency) return total;
    const conversionRate = getGeneralCostConversionRate(data);
    if (conversionRate === null) return null;
    return total * conversionRate;
  };

  const buildGeneralCostFormulaPreview = (data) =>
    buildGeneralCostFormulaPreviewText(data);

  const toggleGeneralCostYear = (year) => {
    setExpandedGeneralCostYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) next.delete(year);
      else next.add(year);
      return next;
    });
  };

  const resetGeneralCostForm = () => {
    setEditingGeneralCostId(null);
    setGeneralCostForm({
      ...createEmptyGeneralCostForm(currentYear),
      currency: defaultSystemCurrency,
    });
  };

  const openCreateGeneralCostModal = () => {
    resetGeneralCostForm();
    fetchExposureClassOptions();
    setShowGeneralCostModal(true);
  };

  const openEditGeneralCostModal = (row, year) => {
    fetchExposureClassOptions();
    setEditingGeneralCostId(row.id);
    setGeneralCostForm({
      year: Number(year),
      cost_category: String(row.costCategory || row.cost_category || ""),
      description: String(row.description || ""),
      calculation_type: String(
        row.calculationType || row.calculation_type || "qty_rate",
      ),
      confined_space: Boolean(row.confinedSpace || row.confined_space),
      quantity_scales_with_teams: Boolean(
        row.quantityScalesWithTeams || row.quantity_scales_with_teams,
      ),
      currency: String(row.currency || defaultSystemCurrency),
      quantity: String(toNum(row.quantity ?? 1)),
      rate: String(
        toNum(row.rate ?? row.costPerUnit ?? row.cost_per_unit ?? 0),
      ),
      rate_per_week: String(toNum(row.ratePerWeek ?? row.rate_per_week ?? 0)),
      hours_per_day: String(toNum(row.hoursPerDay ?? row.hours_per_day ?? 0)),
      day_count: String(toNum(row.dayCount ?? row.day_count ?? 0)),
      movement_frequency_days: String(
        toNum(row.movementFrequencyDays ?? row.movement_frequency_days ?? 0),
      ),
      week_count: String(toNum(row.weekCount ?? row.week_count ?? 0)),
      exposure_class_ids: normalizeExposureIds(
        row.exposureClassIds || row.exposure_class_ids || [],
      ),
      sort_order: String(toNum(row.sortOrder ?? row.sort_order ?? 99)),
    });
    setShowGeneralCostModal(true);
  };

  const closeGeneralCostModal = () => {
    setShowGeneralCostModal(false);
    resetGeneralCostForm();
  };

  const handleSaveGeneralCost = async () => {
    if (!generalCostForm.cost_category.trim()) {
      alert("Cost name is required.");
      return;
    }

    setSavingGeneralCost(true);
    try {
      const payload = {
        year: Number(generalCostForm.year),
        cost_category: generalCostForm.cost_category.trim(),
        description: generalCostForm.description?.trim() || null,
        calculation_type: generalCostForm.calculation_type,
        confined_space: Boolean(generalCostForm.confined_space),
        quantity_scales_with_teams: Boolean(
          generalCostForm.quantity_scales_with_teams,
        ),
        quantity: toNum(generalCostForm.quantity),
        rate: toNum(generalCostForm.rate),
        rate_per_week: toNum(generalCostForm.rate_per_week),
        hours_per_day: toNum(generalCostForm.hours_per_day),
        day_count: toNum(generalCostForm.day_count),
        movement_frequency_days: toNum(generalCostForm.movement_frequency_days),
        week_count: toNum(generalCostForm.week_count),
        exposure_class_ids: normalizeExposureIds(
          generalCostForm.exposure_class_ids,
        ),
        sort_order: toNum(generalCostForm.sort_order),
        currency: generalCostForm.currency || defaultSystemCurrency,
        // keep legacy fields populated for backward compatibility
        cost_per_unit: toNum(generalCostForm.rate),
        unit_type: "custom",
      };

      if (editingGeneralCostId) {
        await api.put(
          `/mobilization-master/costs/${editingGeneralCostId}`,
          payload,
        );
      } else {
        await api.post("/mobilization-master/costs", payload);
      }

      closeGeneralCostModal();
      await fetchPaintersData();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to save general cost.");
    } finally {
      setSavingGeneralCost(false);
    }
  };

  const handleDeleteGeneralCost = async (id) => {
    if (!window.confirm("Delete this general cost row?")) return;
    try {
      await api.delete(`/mobilization-master/costs/${id}`);
      await fetchPaintersData();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete general cost.");
    }
  };

  const handleDeleteJobsYear = async (year) => {
    const group = (jobYears || []).find((g) => g.year === year);
    const jobs = (group && group.jobs) || [];
    if (!jobs.length) {
      alert(`No jobs found for ${year}.`);
      return;
    }
    if (
      !window.confirm(
        `Delete all ${jobs.length} job(s) for ${year}? This cannot be undone.`,
      )
    )
      return;
    try {
      await Promise.all(
        jobs.map((job) =>
          api.delete(`/mobilization-master/painter-jobs/${job.id}`),
        ),
      );
      await fetchPaintersData();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete jobs for year.");
    }
  };

  const handleDeleteTeamsYear = async (year) => {
    const group = (teamYears || []).find((g) => g.year === year);
    const teams = (group && group.teams) || [];
    if (!teams.length) {
      alert(`No team configurations found for ${year}.`);
      return;
    }
    if (
      !window.confirm(
        `Delete all ${teams.length} team configuration(s) for ${year}? This cannot be undone.`,
      )
    )
      return;
    try {
      await Promise.all(
        teams.map((team) =>
          api.delete(`/mobilization-master/painter-team-configs/${team.id}`),
        ),
      );
      await fetchPaintersData();
    } catch (err) {
      alert(
        err.response?.data?.message ||
          "Failed to delete team configurations for year.",
      );
    }
  };

  const handleDeleteProductivityYear = async (year) => {
    const group = (productivityMatrixGrouped || []).find(
      (g) => g.year === year,
    );
    const rows = (group && group.rows) || [];
    const ids = rows.flatMap((row) =>
      Object.values(row.ids || {}).filter(Boolean),
    );
    if (!ids.length) {
      alert(`No productivity rows found for ${year}.`);
      return;
    }
    if (
      !window.confirm(
        `Delete all ${rows.length} productivity row(s) for ${year}? This cannot be undone.`,
      )
    )
      return;
    try {
      await Promise.all(
        ids.map((id) => api.delete(`/mobilization-master/productivity/${id}`)),
      );
      await fetchPaintersData();
    } catch (err) {
      alert(
        err.response?.data?.message ||
          "Failed to delete productivity rows for year.",
      );
    }
  };

  const handleDeleteGeneralCostsYear = async (year) => {
    const group = (generalCostYears || []).find((g) => g.year === year);
    const costs = (group && group.costs) || [];
    if (!costs.length) {
      alert(`No general cost rows found for ${year}.`);
      return;
    }
    if (
      !window.confirm(
        `Delete all ${costs.length} general cost row(s) for ${year}? This cannot be undone.`,
      )
    )
      return;
    try {
      await Promise.all(
        costs.map((row) => api.delete(`/mobilization-master/costs/${row.id}`)),
      );
      await fetchPaintersData();
    } catch (err) {
      alert(
        err.response?.data?.message ||
          "Failed to delete general costs for year.",
      );
    }
  };

  const handleDeleteMobilizationConfigsYear = async (year) => {
    const group = (mobilizationConfigYears || []).find((g) => g.year === year);
    const configs = (group && group.configs) || [];
    if (!configs.length) {
      alert(`No mobilization cost rows found for ${year}.`);
      return;
    }
    if (
      !window.confirm(
        `Delete all ${configs.length} mobilization cost row(s) for ${year}? This cannot be undone.`,
      )
    )
      return;
    try {
      await Promise.all(
        configs.map((row) =>
          api.delete(`/mobilization-master/mobilization-configs/${row.id}`),
        ),
      );
      await fetchPaintersData();
    } catch (err) {
      alert(
        err.response?.data?.message ||
          "Failed to delete mobilization costs for year.",
      );
    }
  };

  const handleCopyGeneralCostsFromYear = (sourceYear) => {
    const source = generalCostYears.find(
      (g) => Number(g.year) === Number(sourceYear),
    );
    if (!source || !(source.costs || []).length) {
      alert(`No general costs found in source year ${sourceYear}.`);
      return;
    }

    const usedYears = new Set(generalCostAllYears.map((y) => Number(y)));
    let suggestedYear = Number(sourceYear) + 1;
    while (usedYears.has(suggestedYear) && suggestedYear <= 3000) {
      suggestedYear += 1;
    }

    setCopyGeneralCostSourceYear(Number(sourceYear));
    setCopyGeneralCostTargetYear(String(suggestedYear));
    setCopyGeneralCostError("");
    setShowCopyGeneralCostModal(true);
  };

  const handleConfirmCopyGeneralCosts = async () => {
    if (!copyGeneralCostSourceYear) return;

    const targetYear = parseInt(copyGeneralCostTargetYear, 10);
    if (
      !Number.isInteger(targetYear) ||
      targetYear < 1900 ||
      targetYear > 3000
    ) {
      setCopyGeneralCostError("Please enter a valid target year.");
      return;
    }
    if (targetYear === copyGeneralCostSourceYear) {
      setCopyGeneralCostError(
        "Target year must be different from source year.",
      );
      return;
    }
    if (generalCostAllYears.includes(targetYear)) {
      setCopyGeneralCostError(
        `Year ${targetYear} already exists. Please choose a new year.`,
      );
      return;
    }

    const source = generalCostYears.find(
      (g) => Number(g.year) === Number(copyGeneralCostSourceYear),
    );
    if (!source || !(source.costs || []).length) {
      setCopyGeneralCostError(
        `No general costs found in source year ${copyGeneralCostSourceYear}.`,
      );
      return;
    }

    try {
      setCopyingGeneralCosts(true);
      setCopyGeneralCostError("");

      const requests = (source.costs || []).map((row, index) => {
        const calculationType = row.calculationType || row.calculation_type;
        const rate = toNum(row.rate ?? row.costPerUnit ?? row.cost_per_unit);
        return api.post("/mobilization-master/costs", {
          year: targetYear,
          cost_category: row.costCategory || row.cost_category,
          description: row.description || null,
          calculation_type: calculationType || "qty_rate",
          confined_space: Boolean(row.confinedSpace || row.confined_space),
          quantity_scales_with_teams: Boolean(
            row.quantityScalesWithTeams || row.quantity_scales_with_teams,
          ),
          quantity: toNum(row.quantity ?? 1),
          rate,
          rate_per_week: toNum(row.ratePerWeek ?? row.rate_per_week),
          hours_per_day: toNum(row.hoursPerDay ?? row.hours_per_day),
          day_count: toNum(row.dayCount ?? row.day_count),
          movement_frequency_days: toNum(
            row.movementFrequencyDays ?? row.movement_frequency_days,
          ),
          week_count: toNum(row.weekCount ?? row.week_count),
          exposure_class_ids: normalizeExposureIds(
            row.exposureClassIds || row.exposure_class_ids || [],
          ),
          sort_order: toNum(row.sortOrder ?? row.sort_order ?? index + 1),
          currency: row.currency || defaultSystemCurrency,
          cost_per_unit: rate,
          unit_type: row.unitType || row.unit_type || "custom",
        });
      });

      const results = await Promise.allSettled(requests);
      const successful = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - successful;

      if (failed > 0) {
        alert(
          `Copied ${successful} of ${results.length} general cost rows. ${failed} failed.`,
        );
      } else {
        alert(
          `Successfully copied ${successful} general cost rows from ${copyGeneralCostSourceYear} to ${targetYear}.`,
        );
      }

      setShowCopyGeneralCostModal(false);
      setCopyGeneralCostSourceYear(null);
      setCopyGeneralCostTargetYear("");
      setCopyGeneralCostError("");
      await fetchPaintersData();
    } catch (err) {
      setCopyGeneralCostError(
        err.response?.data?.message || "Failed to copy general costs.",
      );
    } finally {
      setCopyingGeneralCosts(false);
    }
  };

  const toggleMobilizationConfigYear = (year) => {
    setExpandedMobilizationConfigYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) next.delete(year);
      else next.add(year);
      return next;
    });
  };

  const resolveEligibleTeamsForYear = useCallback(
    (year) => {
      const targetYear = Number(year || currentYear);
      return (teamYears || [])
        .filter((group) => Number(group.year) <= targetYear)
        .sort((a, b) => Number(b.year) - Number(a.year))
        .flatMap((group) =>
          (group.teams || []).map((team) => ({
            id: team.id,
            year: Number(group.year),
            label: `${getTeamDisplayName(team)} (${group.year})`,
          })),
        );
    },
    [currentYear, teamYears],
  );

  const eligibleTeamsForMobilizationForm = useMemo(
    () => resolveEligibleTeamsForYear(mobilizationConfigForm.year),
    [mobilizationConfigForm.year, resolveEligibleTeamsForYear],
  );

  const resolveGeneralCostsForMobilizationYear = useCallback(
    (year) => {
      const targetYear = Number(year || currentYear);
      const sourceGroup = (generalCostYears || [])
        .filter((group) => Number(group.year) <= targetYear)
        .sort((a, b) => Number(b.year) - Number(a.year))[0];

      return {
        sourceYear: sourceGroup ? Number(sourceGroup.year) : null,
        costs: sourceGroup?.costs || [],
      };
    },
    [currentYear, generalCostYears],
  );

  const defaultGeneralCostIdsForYear = useCallback(
    (year) =>
      resolveGeneralCostsForMobilizationYear(year)
        .costs.map((row) => Number(row.id))
        .filter((id) => Number.isInteger(id) && id > 0),
    [resolveGeneralCostsForMobilizationYear],
  );

  const generalCostsForMobilizationForm = useMemo(
    () => resolveGeneralCostsForMobilizationYear(mobilizationConfigForm.year),
    [mobilizationConfigForm.year, resolveGeneralCostsForMobilizationYear],
  );

  const resetMobilizationConfigForm = () => {
    setEditingMobilizationConfigId(null);
    setMobilizationConfigForm({
      ...createEmptyMobilizationConfigForm(currentYear),
      selected_general_cost_ids: defaultGeneralCostIdsForYear(currentYear),
    });
  };

  const openCreateMobilizationConfigModal = () => {
    resetMobilizationConfigForm();
    setShowMobilizationConfigModal(true);
  };

  const openEditMobilizationConfigModal = (row, year) => {
    setEditingMobilizationConfigId(row.id);
    setMobilizationConfigForm({
      year: Number(year),
      team_id: String(row.teamId || row.team_id || row.teamConfig?.id || ""),
      speed_band: String(row.speedBand || row.speed_band || "Medium"),
      selected_general_cost_ids:
        (row.selectedGeneralCostIds || row.selected_general_cost_ids || [])
          .length > 0
          ? (row.selectedGeneralCostIds || row.selected_general_cost_ids || [])
              .map((id) => Number(id))
              .filter((id) => Number.isInteger(id) && id > 0)
          : defaultGeneralCostIdsForYear(year),
      is_active: Boolean(row.isActive || row.is_active),
      sort_order: String(toNum(row.sortOrder || row.sort_order || 99)),
      notes: String(row.notes || ""),
    });
    setShowMobilizationConfigModal(true);
  };

  const closeMobilizationConfigModal = () => {
    setShowMobilizationConfigModal(false);
    resetMobilizationConfigForm();
  };

  const handleSaveMobilizationConfig = async () => {
    if (!mobilizationConfigForm.team_id) {
      alert("Please select a team.");
      return;
    }

    setSavingMobilizationConfig(true);
    try {
      const payload = {
        year: Number(mobilizationConfigForm.year),
        team_id: Number(mobilizationConfigForm.team_id),
        speed_band: mobilizationConfigForm.speed_band,
        selected_general_cost_ids: (
          mobilizationConfigForm.selected_general_cost_ids || []
        )
          .map((id) => Number(id))
          .filter((id) => Number.isInteger(id) && id > 0),
        is_active: Boolean(mobilizationConfigForm.is_active),
        sort_order: toNum(mobilizationConfigForm.sort_order),
        notes: mobilizationConfigForm.notes?.trim() || null,
      };

      if (editingMobilizationConfigId) {
        await api.put(
          `/mobilization-master/mobilization-configs/${editingMobilizationConfigId}`,
          payload,
        );
      } else {
        await api.post("/mobilization-master/mobilization-configs", payload);
      }

      closeMobilizationConfigModal();
      await fetchPaintersData();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to save mobilization cost.");
    } finally {
      setSavingMobilizationConfig(false);
    }
  };

  const handleDeleteMobilizationConfig = async (id) => {
    if (!window.confirm("Delete this mobilization cost row?")) return;
    try {
      await api.delete(`/mobilization-master/mobilization-configs/${id}`);
      await fetchPaintersData();
    } catch (err) {
      alert(
        err.response?.data?.message || "Failed to delete mobilization cost.",
      );
    }
  };

  const handleToggleMobilizationConfigActive = async (row, checked) => {
    try {
      await api.put(`/mobilization-master/mobilization-configs/${row.id}`, {
        is_active: checked,
      });
      await fetchPaintersData();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update active state.");
    }
  };

  const handleCopyMobilizationConfigsFromYear = (sourceYear) => {
    const source = mobilizationConfigYears.find(
      (g) => Number(g.year) === Number(sourceYear),
    );
    if (!source || !(source.configs || []).length) {
      alert(`No mobilization costs found in source year ${sourceYear}.`);
      return;
    }

    const usedYears = new Set(mobilizationConfigAllYears.map((y) => Number(y)));
    let suggestedYear = Number(sourceYear) + 1;
    while (usedYears.has(suggestedYear) && suggestedYear <= 3000) {
      suggestedYear += 1;
    }

    setCopyMobilizationConfigSourceYear(Number(sourceYear));
    setCopyMobilizationConfigTargetYear(String(suggestedYear));
    setCopyMobilizationConfigError("");
    setShowCopyMobilizationConfigModal(true);
  };

  const handleConfirmCopyMobilizationConfigs = async () => {
    if (!copyMobilizationConfigSourceYear) return;

    const targetYear = parseInt(copyMobilizationConfigTargetYear, 10);
    if (
      !Number.isInteger(targetYear) ||
      targetYear < 1900 ||
      targetYear > 3000
    ) {
      setCopyMobilizationConfigError("Please enter a valid target year.");
      return;
    }
    if (targetYear === copyMobilizationConfigSourceYear) {
      setCopyMobilizationConfigError(
        "Target year must be different from source year.",
      );
      return;
    }
    if (mobilizationConfigAllYears.includes(targetYear)) {
      setCopyMobilizationConfigError(
        `Year ${targetYear} already exists. Please choose a new year.`,
      );
      return;
    }

    const source = mobilizationConfigYears.find(
      (g) => Number(g.year) === Number(copyMobilizationConfigSourceYear),
    );
    if (!source || !(source.configs || []).length) {
      setCopyMobilizationConfigError(
        `No mobilization costs found in source year ${copyMobilizationConfigSourceYear}.`,
      );
      return;
    }

    const eligibleTeams = resolveEligibleTeamsForYear(targetYear);
    if (eligibleTeams.length === 0) {
      setCopyMobilizationConfigError(
        `No team configuration exists for year ${targetYear} or previous years.`,
      );
      return;
    }

    try {
      setCopyingMobilizationConfigs(true);
      setCopyMobilizationConfigError("");

      const fallbackTeamId = Number(eligibleTeams[0].id);
      const requests = (source.configs || []).map((row, index) => {
        const sourceTeam = Number(row.teamId || row.team_id || 0);
        const mappedTeam = eligibleTeams.find(
          (team) => Number(team.id) === sourceTeam,
        );
        return api.post("/mobilization-master/mobilization-configs", {
          year: targetYear,
          team_id: mappedTeam ? Number(mappedTeam.id) : fallbackTeamId,
          speed_band: row.speedBand || row.speed_band || "Medium",
          selected_general_cost_ids: defaultGeneralCostIdsForYear(targetYear),
          is_active: Boolean(row.isActive || row.is_active),
          sort_order: toNum(row.sortOrder || row.sort_order || index + 1),
          notes: row.notes || null,
        });
      });

      const results = await Promise.allSettled(requests);
      const successful = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - successful;

      if (failed > 0) {
        alert(
          `Copied ${successful} of ${results.length} mobilization costs. ${failed} failed.`,
        );
      } else {
        alert(
          `Successfully copied ${successful} mobilization costs from ${copyMobilizationConfigSourceYear} to ${targetYear}.`,
        );
      }

      setShowCopyMobilizationConfigModal(false);
      setCopyMobilizationConfigSourceYear(null);
      setCopyMobilizationConfigTargetYear("");
      setCopyMobilizationConfigError("");
      await fetchPaintersData();
    } catch (err) {
      setCopyMobilizationConfigError(
        err.response?.data?.message || "Failed to copy mobilization costs.",
      );
    } finally {
      setCopyingMobilizationConfigs(false);
    }
  };

  const handleSelectSimulationConfig = useCallback((row, year) => {
    if (!SHOW_STANDALONE_MOBILIZATION_SIMULATION) {
      return;
    }

    setSimulationYear(Number(year));
    setSelectedSimulationConfigId(Number(row?.id || 0) || null);
  }, []);

  const simulationConfigsForYear = useMemo(() => {
    if (!simulationYear) return [];
    const group = (mobilizationConfigYears || []).find(
      (item) => Number(item.year) === Number(simulationYear),
    );
    return group?.configs || [];
  }, [mobilizationConfigYears, simulationYear]);

  useEffect(() => {
    if (!mobilizationConfigYears.length) {
      setSimulationYear(null);
      setSelectedSimulationConfigId(null);
      return;
    }

    if (!simulationYear) {
      const firstYear = Number(mobilizationConfigYears[0].year);
      setSimulationYear(firstYear);
      return;
    }

    const yearExists = mobilizationConfigYears.some(
      (item) => Number(item.year) === Number(simulationYear),
    );
    if (!yearExists) {
      setSimulationYear(Number(mobilizationConfigYears[0].year));
      setSelectedSimulationConfigId(null);
    }
  }, [mobilizationConfigYears, simulationYear]);

  const selectedSimulationConfig = useMemo(() => {
    if ((simulationConfigsForYear || []).length === 0) return null;
    const selected = simulationConfigsForYear.find(
      (row) => Number(row.id) === Number(selectedSimulationConfigId),
    );
    if (selected) return selected;
    return simulationConfigsForYear[0];
  }, [selectedSimulationConfigId, simulationConfigsForYear]);

  useEffect(() => {
    if (!simulationConfigsForYear.length) {
      setSelectedSimulationConfigId(null);
      return;
    }

    const exists = simulationConfigsForYear.some(
      (row) => Number(row.id) === Number(selectedSimulationConfigId),
    );
    if (!exists) {
      setSelectedSimulationConfigId(Number(simulationConfigsForYear[0].id));
    }
  }, [selectedSimulationConfigId, simulationConfigsForYear]);

  const simulationGeneralCostContext = useMemo(
    () => resolveGeneralCostsForMobilizationYear(simulationYear || currentYear),
    [currentYear, resolveGeneralCostsForMobilizationYear, simulationYear],
  );

  const selectedSimulationGeneralCosts = useMemo(() => {
    const all = simulationGeneralCostContext?.costs || [];
    const selectedIds = (
      selectedSimulationConfig?.selectedGeneralCostIds ||
      selectedSimulationConfig?.selected_general_cost_ids ||
      []
    )
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0);

    if (selectedIds.length === 0) return all;
    const set = new Set(selectedIds);
    return all.filter((row) => set.has(Number(row.id)));
  }, [selectedSimulationConfig, simulationGeneralCostContext]);

  const selectedSimulationTeamConfig = useMemo(() => {
    const teamId = Number(
      selectedSimulationConfig?.teamId ||
        selectedSimulationConfig?.team_id ||
        selectedSimulationConfig?.teamConfig?.id ||
        0,
    );
    if (!teamId) return null;

    for (const group of teamYears || []) {
      const team = (group.teams || []).find(
        (item) => Number(item.id) === Number(teamId),
      );
      if (team) {
        return { ...team, sourceYear: Number(group.year) };
      }
    }
    return null;
  }, [selectedSimulationConfig, teamYears]);

  const selectedSimulationProductivity = useMemo(() => {
    if (!selectedSimulationConfig) return null;
    const targetYear = Number(simulationYear || selectedSimulationConfig.year);
    const steelType = String(
      selectedSimulationConfig.steelType ||
        selectedSimulationConfig.steel_type ||
        "",
    ).toLowerCase();
    const coatType = String(
      selectedSimulationConfig.coatType ||
        selectedSimulationConfig.coat_type ||
        "",
    ).toLowerCase();
    const speedBand = String(
      selectedSimulationConfig.speedBand ||
        selectedSimulationConfig.speed_band ||
        "",
    ).toLowerCase();
    const appKey = String(
      selectedSimulationConfig.applicationType ||
        selectedSimulationConfig.application_type ||
        "",
    )
      .toLowerCase()
      .trim();

    const matches = (productivityMatrixByYear || [])
      .filter(
        (row) =>
          Number(row.year) <= targetYear &&
          String(row.steel_size || "").toLowerCase() === steelType &&
          String(row.coat_type || "").toLowerCase() === coatType &&
          String(row.speed_band || "").toLowerCase() === speedBand,
      )
      .sort((a, b) => Number(b.year) - Number(a.year));

    if (matches.length === 0) return null;
    const row = matches[0];
    const sqftPerHour = toNum(row?.sqft?.[appKey]);
    const sqmPerHour = sqftPerHour * SQFT_TO_SQM;
    const manhoursPerSqm = sqmPerHour > 0 ? 1 / sqmPerHour : null;

    return {
      sourceYear: Number(row.year),
      applicationType: appKey,
      sqftPerHour,
      sqmPerHour,
      manhoursPerSqm,
    };
  }, [productivityMatrixByYear, selectedSimulationConfig, simulationYear]);

  const simulationCombinationRows = useMemo(() => {
    const rows = (simulationCombinations || []).map((row) => {
      const corrosionCategory = row.corrosionCategory || {};
      const damageSeverity = row.damageSeverity || {};

      return {
        id: Number(row.id),
        code: String(row.combinationCode || row.combination_code || "").trim(),
        exposureClassId: Number(
          corrosionCategory.id ||
            row.corrosionCategoryId ||
            row.corrosion_category_id ||
            0,
        ),
        exposureClassCode: String(
          corrosionCategory.categoryCode ||
            corrosionCategory.category_code ||
            "",
        ).trim(),
        exposureClassName: String(
          corrosionCategory.categoryName ||
            corrosionCategory.category_name ||
            "",
        ).trim(),
        damageSeverityCode: String(
          damageSeverity.severityCode || damageSeverity.severity_code || "",
        ).trim(),
        raw: row,
      };
    });

    return rows
      .filter((row) => row.id > 0 && row.code)
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [simulationCombinations]);

  const simulationExposureClasses = useMemo(() => {
    const map = new Map();

    simulationCombinationRows.forEach((row) => {
      if (!row.exposureClassCode) return;
      if (map.has(row.exposureClassCode)) return;

      map.set(row.exposureClassCode, {
        id: row.exposureClassId,
        code: row.exposureClassCode,
        name: row.exposureClassName,
      });
    });

    return Array.from(map.values()).sort((a, b) =>
      a.code.localeCompare(b.code),
    );
  }, [simulationCombinationRows]);

  const simulationDamageSplitByExposureClassCode = useMemo(() => {
    const map = new Map();

    (simulationDamageSplits || []).forEach((row) => {
      const code = String(row.corrosionCategory || "").trim();
      if (!code) return;

      map.set(code, {
        severityA: Number(row.severityA || 0),
        severityB: Number(row.severityB || 0),
        severityC: Number(row.severityC || 0),
      });
    });

    return map;
  }, [simulationDamageSplits]);

  useEffect(() => {
    if (!simulationExposureClasses.length) {
      setSimulationExposureClassEnabledByCode({});
      return;
    }

    setSimulationExposureClassEnabledByCode((prev) => {
      const next = {};

      simulationExposureClasses.forEach((row) => {
        next[row.code] = prev[row.code] !== undefined ? prev[row.code] : true;
      });

      return next;
    });
  }, [simulationExposureClasses]);

  useEffect(() => {
    if (!simulationExposureClasses.length) {
      setSimulationDamagedAreaSqmByExposureClassCode({});
      return;
    }

    setSimulationDamagedAreaSqmByExposureClassCode((prev) => {
      const next = {};
      const exposureClassCodes = simulationExposureClasses
        .map((row) => String(row.code || "").trim())
        .filter(Boolean);
      const hasExistingValues = exposureClassCodes.some((code) => {
        const value = prev[code];
        return value !== undefined && value !== "";
      });

      exposureClassCodes.forEach((code, index) => {
        if (prev[code] !== undefined) {
          next[code] = prev[code];
          return;
        }

        next[code] = hasExistingValues
          ? "1000"
          : String(getDefaultSimulationDamagedAreaSqm(index));
      });

      return next;
    });
  }, [simulationExposureClasses]);

  const activeSimulationExposureClassCodes = useMemo(
    () =>
      simulationExposureClasses
        .filter(
          (row) => simulationExposureClassEnabledByCode[row.code] !== false,
        )
        .map((row) => row.code),
    [simulationExposureClassEnabledByCode, simulationExposureClasses],
  );

  const activeSimulationExposureClassIds = useMemo(
    () =>
      simulationExposureClasses
        .filter(
          (row) => simulationExposureClassEnabledByCode[row.code] !== false,
        )
        .map((row) => Number(row.id))
        .filter((id) => Number.isInteger(id) && id > 0),
    [simulationExposureClassEnabledByCode, simulationExposureClasses],
  );

  const activeSimulationExposureClassCount =
    activeSimulationExposureClassCodes.length;

  const simulationTotalAreaSqm = useMemo(
    () =>
      activeSimulationExposureClassCodes.reduce((sum, code) => {
        const numericValue = Number(
          simulationDamagedAreaSqmByExposureClassCode[code] || 0,
        );
        return (
          sum + (Number.isFinite(numericValue) ? Math.max(0, numericValue) : 0)
        );
      }, 0),
    [
      activeSimulationExposureClassCodes,
      simulationDamagedAreaSqmByExposureClassCode,
    ],
  );

  const visibleSimulationCombinationRows = useMemo(() => {
    if (!simulationExposureClasses.length) return simulationCombinationRows;
    if (!activeSimulationExposureClassCodes.length) return [];

    const activeCodeSet = new Set(activeSimulationExposureClassCodes);
    return simulationCombinationRows.filter((row) => {
      if (!row.exposureClassCode) return true;
      return activeCodeSet.has(row.exposureClassCode);
    });
  }, [
    activeSimulationExposureClassCodes,
    simulationCombinationRows,
    simulationExposureClasses,
  ]);

  const simulationGeneralCostsForActiveExposure = useMemo(() => {
    if (!simulationExposureClasses.length)
      return selectedSimulationGeneralCosts;

    const activeIdSet = new Set(activeSimulationExposureClassIds);
    return selectedSimulationGeneralCosts.filter((row) => {
      const exposureIds = normalizeExposureIds(
        row.exposureClassIds || row.exposure_class_ids || [],
      );

      if (exposureIds.length === 0) return true;
      return exposureIds.some((id) => activeIdSet.has(id));
    });
  }, [
    activeSimulationExposureClassIds,
    selectedSimulationGeneralCosts,
    simulationExposureClasses,
  ]);

  const handleToggleSimulationExposureClass = useCallback((code) => {
    setSimulationExposureClassEnabledByCode((prev) => {
      const isEnabled = prev[code] !== false;
      const activeCount = Object.values(prev).reduce(
        (sum, value) => sum + (value !== false ? 1 : 0),
        0,
      );

      if (isEnabled && activeCount <= 1) {
        return prev;
      }

      return {
        ...prev,
        [code]: !isEnabled,
      };
    });
  }, []);

  const getDamageSplitDefaultForCombination = useCallback(
    (combination) => {
      const classDefaults = simulationDamageSplitByExposureClassCode.get(
        combination.exposureClassCode,
      );
      if (!classDefaults) return 0;

      const severityCode = String(combination.damageSeverityCode || "")
        .trim()
        .toUpperCase();

      if (severityCode === "A") return classDefaults.severityA;
      if (severityCode === "B") return classDefaults.severityB;
      if (severityCode === "C") return classDefaults.severityC;
      return 0;
    },
    [simulationDamageSplitByExposureClassCode],
  );

  useEffect(() => {
    if (!simulationCombinationRows.length) {
      setSimulationAreaSplitPctByCombinationId({});
      return;
    }

    setSimulationAreaSplitPctByCombinationId((prev) => {
      const next = {};

      simulationCombinationRows.forEach((row) => {
        const existing = prev[row.id];
        const defaultSplitPct = getDamageSplitDefaultForCombination(row);
        next[row.id] =
          existing !== undefined && existing !== ""
            ? existing
            : Number.isFinite(defaultSplitPct)
              ? String(defaultSplitPct)
              : "";
      });

      return next;
    });
  }, [getDamageSplitDefaultForCombination, simulationCombinationRows]);

  const repairProcessTypeNameById = useMemo(() => {
    const map = new Map();
    (simulationRepairProcessTypes || []).forEach((row) => {
      const id = Number(row.id);
      if (!Number.isInteger(id) || id <= 0) return;
      const name = String(row.processName || row.process_name || "").trim();
      if (!name) return;
      map.set(id, name);
    });
    return map;
  }, [simulationRepairProcessTypes]);

  const paintLayerSummaryByCombinationId = useMemo(() => {
    const map = new Map();

    (simulationRepairRules || []).forEach((rule) => {
      const combinationId = Number(
        rule.corrosionDamageCombinationId ||
          rule.corrosion_damage_combination_id ||
          0,
      );
      const processTypeId = Number(
        rule.repairProcessTypeId || rule.repair_process_type_id || 0,
      );
      const isRequired = Boolean(rule.isRequired ?? rule.is_required);
      const isRuleActive = Boolean(rule.isActive ?? rule.is_active ?? true);

      if (!combinationId || !processTypeId || !isRequired || !isRuleActive) {
        return;
      }

      const processName = repairProcessTypeNameById.get(processTypeId) || "";
      const layerLabel = getPaintLayerDisplayName(processName);
      if (!layerLabel) return;

      const effectivePct = getRepairRuleLayerProportion(rule);
      const current = map.get(combinationId) || {
        layerCount: 0,
        effectiveLayerUnits: 0,
        customProportions: [],
      };

      current.layerCount += 1;
      current.effectiveLayerUnits += effectivePct / 100;

      if (hasRepairRuleLayerProportion(rule)) {
        current.customProportions.push({
          layerLabel,
          pct: effectivePct,
        });
      }

      map.set(combinationId, current);
    });

    return map;
  }, [repairProcessTypeNameById, simulationRepairRules]);

  const simulationCustomLayerProportionSummary = useMemo(() => {
    const entries = [];

    visibleSimulationCombinationRows.forEach((combination) => {
      const layerSummary =
        paintLayerSummaryByCombinationId.get(combination.id) || null;
      const customProportions = layerSummary?.customProportions || [];
      if (!customProportions.length) return;

      const formatPct = (value) => {
        const numericValue = Number(value || 0);
        return Math.abs(numericValue - Math.round(numericValue)) < 0.0001
          ? `${formatMoney(numericValue, 0)}%`
          : `${formatMoney(numericValue, 2)}%`;
      };

      customProportions.forEach((item) => {
        entries.push(
          `${combination.code}-${item.layerLabel} ${formatPct(item.pct)}`,
        );
      });
    });

    return entries;
  }, [paintLayerSummaryByCombinationId, visibleSimulationCombinationRows]);

  const simulationAreaDistribution = useMemo(() => {
    const totalPctByExposureClass = {};

    visibleSimulationCombinationRows.forEach((combination) => {
      const pct = Number(
        simulationAreaSplitPctByCombinationId[combination.id] || 0,
      );
      const code = String(combination.exposureClassCode || "").trim();
      if (!code) return;

      totalPctByExposureClass[code] =
        Number(totalPctByExposureClass[code] || 0) + pct;
    });

    const totalsByExposureClass = activeSimulationExposureClassCodes.map(
      (code) => {
        const totalPct = Number(totalPctByExposureClass[code] || 0);

        return {
          code,
          totalPct,
          isValid: Math.abs(totalPct - 100) < 0.0001,
        };
      },
    );

    const invalidExposureClassCodes = totalsByExposureClass
      .filter((row) => !row.isValid)
      .map((row) => row.code);

    const rows = visibleSimulationCombinationRows.map((combination) => {
      const rawPct = simulationAreaSplitPctByCombinationId[combination.id];
      const pct = Number(rawPct || 0);
      const layerSummary =
        paintLayerSummaryByCombinationId.get(combination.id) || null;
      const layerCount = Number(layerSummary?.layerCount || 0);
      const effectiveLayerUnits = Number(
        layerSummary?.effectiveLayerUnits || 0,
      );
      const exposureClassCode = String(
        combination.exposureClassCode || "",
      ).trim();
      const exposureClassTotalPct = Number(
        totalPctByExposureClass[exposureClassCode] || 0,
      );
      const exposureClassAreaSqm = Number(
        simulationDamagedAreaSqmByExposureClassCode[exposureClassCode] || 0,
      );
      const damagedAreaSqm =
        exposureClassTotalPct > 0
          ? Math.max(0, exposureClassAreaSqm) * (pct / exposureClassTotalPct)
          : 0;
      const paintedAreaSqm = damagedAreaSqm * effectiveLayerUnits;

      return {
        ...combination,
        pct,
        exposureClassAreaSqm,
        exposureClassTotalPct,
        layerCount,
        effectiveLayerUnits,
        damagedAreaSqm,
        paintedAreaSqm,
      };
    });

    const totalPct = rows.reduce((sum, row) => sum + Number(row.pct || 0), 0);
    const totalPaintedAreaSqm = rows.reduce(
      (sum, row) => sum + Number(row.paintedAreaSqm || 0),
      0,
    );

    return {
      rows,
      totalPct,
      totalsByExposureClass,
      invalidExposureClassCodes,
      totalPaintedAreaSqm,
      pctIsValid: invalidExposureClassCodes.length === 0,
    };
  }, [
    activeSimulationExposureClassCodes,
    paintLayerSummaryByCombinationId,
    simulationDamagedAreaSqmByExposureClassCode,
    simulationAreaSplitPctByCombinationId,
    visibleSimulationCombinationRows,
  ]);

  const simulationResult = useMemo(() => {
    const totalArea = Number(simulationTotalAreaSqm || 0);
    const weekLimit = Number(simulationWeekLimit || 35);

    if (!selectedSimulationConfig) {
      return { error: "No mobilization cost row selected." };
    }
    if (!selectedSimulationTeamConfig) {
      return { error: "Selected team configuration was not found." };
    }
    if (!selectedSimulationProductivity?.manhoursPerSqm) {
      return {
        error:
          "No matching productivity row found for steel/coat/speed/application.",
      };
    }
    if (totalArea <= 0) {
      return {
        error:
          "Enter damaged area greater than 0 for at least one active exposure class.",
      };
    }
    if (
      simulationExposureClasses.length > 0 &&
      activeSimulationExposureClassCodes.length === 0
    ) {
      return { error: "Turn on at least one exposure class." };
    }
    if (!simulationAreaDistribution.rows.length) {
      return {
        error:
          "No corrosion-damage combinations found for simulation area split.",
      };
    }
    if (!simulationAreaDistribution.pctIsValid) {
      const invalidCodes =
        simulationAreaDistribution.invalidExposureClassCodes || [];
      return {
        error:
          invalidCodes.length > 0
            ? `Damage combination area split must total exactly 100% per active exposure class. Check: ${invalidCodes.join(", ")}.`
            : "Damage combination area split must total exactly 100% per active exposure class.",
      };
    }
    if (weekLimit <= 0) {
      return { error: "# Weeks limit must be greater than 0." };
    }

    const allocations =
      selectedSimulationTeamConfig.jobAllocations ||
      selectedSimulationTeamConfig.job_allocations ||
      [];

    const {
      teamPainterCount,
      teamPaintingHoursPerWeek,
      perTeamForemanPaintingHoursPerWeek,
      perTeamNonForemanPaintingHoursPerWeek,
      teamWeeklyLaborCost,
      perTeamForemanWeeklyCost,
      perTeamNonForemanWeeklyCost,
    } = calculateTeamWeeklyMetrics(allocations);

    if (teamPaintingHoursPerWeek <= 0) {
      return {
        error:
          "Team painting capacity is zero. Check member count, hours/week, and painting time %.",
      };
    }

    const paintedAreaSqm = simulationAreaDistribution.totalPaintedAreaSqm;
    if (paintedAreaSqm <= 0) {
      return {
        error:
          "Painted area is zero. Check repair rules (active paint layers and proportions) and area split.",
      };
    }

    const requiredManhours =
      paintedAreaSqm * selectedSimulationProductivity.manhoursPerSqm;

    const durationResult = resolveTeamsAndWeeks({
      requiredManhours,
      weekLimit,
      foremansLimit: simulationForemansLimit,
      perTeamNonForemanPaintingHoursPerWeek,
      perTeamForemanPaintingHoursPerWeek,
    });

    if (durationResult.error) {
      return {
        error: durationResult.error,
      };
    }

    const { teamsRequired, weeksRequired, foremansLimit } = durationResult;

    const teamsWithForemanCost =
      perTeamForemanWeeklyCost > 0 ? Math.min(teamsRequired, foremansLimit) : 0;

    const generalCostBreakdown = simulationGeneralCostsForActiveExposure.map(
      (row) => {
        const total = calculateGeneralCostBaseTotal({
          data: row,
          weeksRequired,
          teamsRequired,
        });

        const conversionRate = getGeneralCostConversionRate(row);
        const convertedTotal =
          conversionRate === null ? null : total * Number(conversionRate || 1);

        return {
          id: row.id,
          name: row.costCategory || row.cost_category,
          convertedTotal,
        };
      },
    );

    const generalCostsTotal = generalCostBreakdown.reduce(
      (sum, row) => sum + Number(row.convertedTotal || 0),
      0,
    );
    const teamLaborTotal =
      (perTeamNonForemanWeeklyCost * teamsRequired +
        perTeamForemanWeeklyCost * teamsWithForemanCost) *
      weeksRequired;
    const totalCost = teamLaborTotal + generalCostsTotal;

    return {
      totalArea,
      paintedAreaSqm,
      weekLimit,
      requiredManhours,
      teamPainterCount,
      teamPaintingHoursPerWeek,
      teamWeeklyLaborCost,
      teamsRequired,
      foremansLimit,
      teamsWithForemanCost,
      weeksRequired,
      teamLaborTotal,
      generalCostsTotal,
      totalCost,
      generalCostBreakdown,
      areaDistributionRows: simulationAreaDistribution.rows,
      areaDistributionPct: simulationAreaDistribution.totalPct,
    };
  }, [
    getGeneralCostConversionRate,
    activeSimulationExposureClassCodes,
    simulationAreaDistribution,
    simulationExposureClasses,
    simulationGeneralCostsForActiveExposure,
    selectedSimulationConfig,
    selectedSimulationProductivity,
    selectedSimulationTeamConfig,
    simulationForemansLimit,
    simulationTotalAreaSqm,
    simulationWeekLimit,
  ]);

  const renderMobilizationCostsTab = () => (
    <div
      className={
        SHOW_STANDALONE_MOBILIZATION_SIMULATION
          ? "grid grid-cols-1 gap-3 xl:grid-cols-[minmax(29rem,_32rem)_minmax(0,_1fr)]"
          : "mx-auto w-full max-w-6xl"
      }
    >
      <div className="w-full bg-white border rounded p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900">
            <span>Teams & Productivity Settings</span>
            <InlineFieldTooltip
              page="mobilization_costs_management"
              section="mobilization_costs"
              fieldKey="mobilization_costs"
            />
          </h3>
          <button
            type="button"
            onClick={openCreateMobilizationConfigModal}
            className="px-3 py-1.5 rounded bg-slate-700 text-white hover:bg-slate-800 text-sm"
          >
            Add New Configuration
          </button>
        </div>

        <div className="border rounded overflow-hidden">
          <table className="w-full table-fixed text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-2.5 py-2 w-8"></th>
                <th className="text-left px-2.5 py-2 w-14">Year</th>
                <th className="text-left px-2.5 py-2">Details</th>
                <th className="text-left px-2.5 py-2 w-14">Active</th>
                <th className="text-left px-2.5 py-2 w-28">Actions</th>
              </tr>
            </thead>
            <tbody>
              {mobilizationConfigYears.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-6 text-center text-gray-500"
                  >
                    No mobilization costs yet.
                  </td>
                </tr>
              )}

              {mobilizationConfigYears.map((group) => {
                const isExpanded = expandedMobilizationConfigYears.has(
                  group.year,
                );
                return (
                  <React.Fragment key={`mobilization-cost-year-${group.year}`}>
                    <tr className="bg-gray-50 hover:bg-gray-100">
                      <td className="px-2.5 py-2">
                        <button
                          type="button"
                          onClick={() =>
                            toggleMobilizationConfigYear(group.year)
                          }
                          className="text-gray-500 hover:text-gray-700"
                          title={isExpanded ? "Collapse year" : "Expand year"}
                        >
                          <FontAwesomeIcon
                            icon={isExpanded ? faChevronDown : faChevronRight}
                          />
                        </button>
                      </td>
                      <td className="px-2.5 py-2 text-sm font-semibold text-gray-900">
                        {group.year}
                      </td>
                      <td
                        colSpan={2}
                        className="px-2.5 py-2 text-sm text-gray-600"
                      >
                        {(group.configs || []).length} mobilization cost rows
                      </td>
                      <td className="px-2.5 py-2 text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              handleCopyMobilizationConfigsFromYear(group.year)
                            }
                            className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                          >
                            Copy
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handleDeleteMobilizationConfigsYear(group.year)
                            }
                            className="text-red-600 hover:text-red-900"
                            title={`Delete all mobilization cost rows for ${group.year}`}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {isExpanded &&
                      (group.configs || []).map((row) => {
                        const teamName = resolveTeamDisplayName(
                          row.teamConfig || row.teamId || row.team_id,
                          "-",
                        );
                        const isSelected =
                          SHOW_STANDALONE_MOBILIZATION_SIMULATION &&
                          Number(row.id) === Number(selectedSimulationConfigId);

                        return (
                          <tr
                            key={`mobilization-cost-row-${row.id}`}
                            className={
                              isSelected
                                ? "bg-blue-50"
                                : "bg-white hover:bg-gray-50"
                            }
                            onClick={
                              SHOW_STANDALONE_MOBILIZATION_SIMULATION
                                ? () =>
                                    handleSelectSimulationConfig(
                                      row,
                                      group.year,
                                    )
                                : undefined
                            }
                            title={
                              SHOW_STANDALONE_MOBILIZATION_SIMULATION
                                ? "Select row to recalculate simulation"
                                : undefined
                            }
                          >
                            <td className="px-2.5 py-2"></td>
                            <td className="px-2.5 py-2 text-gray-700">
                              {row.year}
                            </td>
                            <td className="px-2.5 py-2 text-gray-900 break-words">
                              <div className="font-medium leading-5">
                                {teamName}
                              </div>
                              <div className="text-xs text-gray-600 mt-0.5 leading-5">
                                {`${row.speedBand || row.speed_band || "-"}`}
                              </div>
                            </td>
                            <td className="px-2.5 py-2 text-gray-700">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleMobilizationConfigActive(
                                    row,
                                    !Boolean(row.isActive || row.is_active),
                                  );
                                }}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                  Boolean(row.isActive || row.is_active)
                                    ? "bg-blue-600"
                                    : "bg-gray-300"
                                }`}
                                title={
                                  Boolean(row.isActive || row.is_active)
                                    ? "Deactivate"
                                    : "Activate"
                                }
                                aria-label={`Toggle active for row ${row.id}`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    Boolean(row.isActive || row.is_active)
                                      ? "translate-x-6"
                                      : "translate-x-1"
                                  }`}
                                />
                              </button>
                            </td>
                            <td className="px-2.5 py-2 whitespace-nowrap">
                              <button
                                type="button"
                                className="text-blue-600 hover:text-blue-900"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditMobilizationConfigModal(
                                    row,
                                    group.year,
                                  );
                                }}
                                title="Edit row"
                              >
                                <FontAwesomeIcon
                                  icon={faPencil}
                                  className="h-4 w-4"
                                />
                              </button>
                              <button
                                type="button"
                                className="text-red-600 hover:text-red-900 ml-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteMobilizationConfig(row.id);
                                }}
                                title="Delete row"
                              >
                                <FontAwesomeIcon
                                  icon={faTrash}
                                  className="h-4 w-4"
                                />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {SHOW_STANDALONE_MOBILIZATION_SIMULATION && (
        <div className="min-w-0 bg-white border rounded p-3 space-y-3">
          <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900">
            <span>
              Standalone Mobilization Cost Simulation{" "}
              {simulationYear ? `(${simulationYear})` : ""}
            </span>
            <InlineFieldTooltip
              page="mobilization_costs_management"
              section="simulation"
              fieldKey="mobilization_cost_simulation_disconnected"
            />
          </h3>

          <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="shrink-0 font-medium text-gray-700">
                Teams & Productivity Configuration:
              </span>
              <span className="min-w-0 truncate text-gray-800">
                {selectedSimulationConfig
                  ? `${resolveTeamDisplayName(selectedSimulationConfig.teamConfig || selectedSimulationConfig.teamId || selectedSimulationConfig.team_id, "Team")} | ${selectedSimulationConfig.speedBand || selectedSimulationConfig.speed_band} ${Boolean(selectedSimulationConfig.isActive || selectedSimulationConfig.is_active) ? "" : "(Inactive)"}`
                  : "Select a mobilization cost row from the left panel"}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-700 mb-1">
                # Weeks Limit
              </label>
              <input
                type="number"
                step="1"
                min="1"
                value={simulationWeekLimit}
                onChange={(e) => setSimulationWeekLimit(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-700 mb-1">
                # Foremen
              </label>
              <input
                type="number"
                step="1"
                min="0"
                value={simulationForemansLimit}
                onChange={(e) => setSimulationForemansLimit(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              />
            </div>
          </div>

          <div className="border border-gray-200 rounded p-3">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between mb-2">
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900">
                  Damage Combination Area Split (%)
                </div>
                {simulationExposureClasses.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {simulationExposureClasses.map((exposureClass) => {
                      const code = String(exposureClass.code || "").trim();
                      const isEnabled =
                        simulationExposureClassEnabledByCode[code] !== false;
                      const areaValue =
                        simulationDamagedAreaSqmByExposureClassCode[code] || "";
                      const areaInputWidthCh = Math.max(
                        4,
                        String(areaValue || "0").length + 1,
                      );
                      const areaInputWidth = `calc(${areaInputWidthCh}ch + 1.75rem)`;

                      return (
                        <div
                          key={`simulation-exposure-class-${code}`}
                          className={`inline-flex items-center gap-2 rounded-lg border px-2.5 py-1.5 ${
                            isEnabled
                              ? "border-blue-200 bg-blue-50"
                              : "border-gray-300 bg-gray-50"
                          }`}
                        >
                          <span className="text-xs font-semibold text-gray-800">
                            {code}
                          </span>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={isEnabled}
                            aria-label={`Toggle ${code}`}
                            disabled={
                              isEnabled &&
                              activeSimulationExposureClassCount <= 1
                            }
                            onClick={() =>
                              handleToggleSimulationExposureClass(code)
                            }
                            className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${
                              isEnabled ? "bg-blue-600" : "bg-gray-300"
                            }`}
                            title={
                              isEnabled &&
                              activeSimulationExposureClassCount <= 1
                                ? `At least one exposure class must remain active`
                                : `Toggle ${code}`
                            }
                          >
                            <span
                              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                                isEnabled ? "translate-x-5" : "translate-x-1"
                              }`}
                            />
                          </button>
                          <input
                            type="number"
                            step="1"
                            min="0"
                            inputMode="numeric"
                            disabled={!isEnabled}
                            value={areaValue}
                            onChange={(e) =>
                              setSimulationDamagedAreaSqmByExposureClassCode(
                                (prev) => ({
                                  ...prev,
                                  [code]: normalizeSimulationDamagedAreaSqm(
                                    e.target.value,
                                  ),
                                }),
                              )
                            }
                            onBlur={(e) =>
                              setSimulationDamagedAreaSqmByExposureClassCode(
                                (prev) => ({
                                  ...prev,
                                  [code]: normalizeSimulationDamagedAreaSqm(
                                    e.target.value,
                                  ),
                                }),
                              )
                            }
                            aria-label={`Total Damaged Area ${code} (sqm)`}
                            style={{ width: areaInputWidth }}
                            className={`min-w-[4ch] px-2 py-1 border rounded text-sm text-right ${
                              isEnabled
                                ? "border-gray-300 bg-white text-gray-900"
                                : "border-gray-200 bg-gray-100 text-gray-500"
                            }`}
                          />
                          <span className="text-xs text-gray-500">sqm</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {simulationAreaDistribution.totalsByExposureClass?.length > 0 ? (
                <div className="flex flex-wrap justify-end gap-2">
                  {simulationAreaDistribution.totalsByExposureClass.map(
                    (row) => (
                      <div
                        key={`simulation-exposure-total-${row.code}`}
                        className={`text-xs font-medium rounded-full px-2 py-1 ${
                          row.isValid
                            ? "text-emerald-700 bg-emerald-50"
                            : "text-amber-700 bg-amber-50"
                        }`}
                      >
                        {row.code}: {formatMoney(row.totalPct, 2)}%
                      </div>
                    ),
                  )}
                </div>
              ) : (
                <div
                  className={`text-xs font-medium ${
                    simulationAreaDistribution.pctIsValid
                      ? "text-emerald-700"
                      : "text-amber-700"
                  }`}
                >
                  Total: {formatMoney(simulationAreaDistribution.totalPct, 2)}%
                </div>
              )}
            </div>

            <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3">
              {visibleSimulationCombinationRows.map((combination) => {
                const row = simulationAreaDistribution.rows.find(
                  (item) => item.id === combination.id,
                );
                const layerCount = row?.layerCount || 0;

                return (
                  <div
                    key={`sim-combo-split-${combination.id}`}
                    className="border border-gray-200 rounded px-2.5 py-2"
                  >
                    <div className="grid grid-cols-[minmax(0,1fr)_56px] items-center gap-2">
                      <div className="min-w-0 text-sm text-gray-900 leading-5">
                        <span className="font-medium">{combination.code}</span>{" "}
                        <span className="text-xs text-gray-500">
                          ({layerCount} Layers)
                        </span>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={
                          simulationAreaSplitPctByCombinationId[
                            combination.id
                          ] || ""
                        }
                        onChange={(e) =>
                          setSimulationAreaSplitPctByCombinationId((prev) => ({
                            ...prev,
                            [combination.id]: e.target.value,
                          }))
                        }
                        className="w-full px-1.5 py-1 border border-gray-300 rounded text-sm text-right"
                        placeholder="0"
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="mt-2 text-xs text-gray-500">
              Painted area uses this formula: damaged area split x active paint
              layers from Repair Rules (excluding Wash), with each layer using
              its configured proportion and defaulting to 100% when blank.
            </p>
            {simulationCustomLayerProportionSummary.length > 0 && (
              <p className="mt-1 text-xs text-gray-500">
                Configured Layer Proportions (based on Repair Rules):{" "}
                {simulationCustomLayerProportionSummary.join(", ")}
              </p>
            )}
          </div>

          {simulationResult?.error ? (
            <div className="bg-amber-50 text-amber-700 border border-amber-200 rounded p-3 text-sm">
              {simulationResult.error}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="border border-gray-200 rounded p-3">
                  <div className="text-xs text-gray-500">Teams Required</div>
                  <div className="text-xl font-semibold text-gray-900">
                    {toInt(simulationResult.teamsRequired)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Team size: {toInt(simulationResult.teamPainterCount)} people
                  </div>
                </div>
                <div className="border border-gray-200 rounded p-3">
                  <div className="text-xs text-gray-500">Weeks Needed</div>
                  <div className="text-xl font-semibold text-gray-900">
                    {formatMoney(simulationResult.weeksRequired, 2)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Limit: {formatMoney(simulationResult.weekLimit, 0)}
                  </div>
                </div>
                <div className="border border-blue-200 bg-blue-50 rounded p-3">
                  <div className="text-xs text-blue-700">
                    Total Cost ({defaultSystemCurrency})
                  </div>
                  <div className="text-xl font-semibold text-blue-900">
                    ${formatWholeNumber(simulationResult.totalCost)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="border border-gray-200 rounded p-3">
                  <div className="font-medium text-gray-900 mb-2">
                    Productivity
                  </div>
                  <div className="text-gray-700">
                    Source year:{" "}
                    {selectedSimulationProductivity?.sourceYear || "-"}
                  </div>
                  <div className="text-gray-700">
                    sqm/hr:{" "}
                    {formatMoney(
                      selectedSimulationProductivity?.sqmPerHour || 0,
                      4,
                    )}
                  </div>
                  <div className="text-gray-700">
                    manhour/sqm:{" "}
                    {formatMoney(
                      selectedSimulationProductivity?.manhoursPerSqm || 0,
                      4,
                    )}
                  </div>
                </div>

                <div className="border border-gray-200 rounded p-3">
                  <div className="font-medium text-gray-900 mb-2">Capacity</div>
                  <div className="text-gray-700">
                    Required manhours:{" "}
                    {formatMoney(simulationResult.requiredManhours, 2)}
                  </div>
                  <div className="text-gray-700">
                    Painted area (sqm):{" "}
                    {formatMoney(simulationResult.paintedAreaSqm, 2)}
                  </div>
                  <div className="text-gray-700">
                    Painting hours/week/team:{" "}
                    {formatMoney(simulationResult.teamPaintingHoursPerWeek, 2)}
                  </div>
                  <div className="text-gray-700">
                    Weekly labor/team: $
                    {formatMoney(simulationResult.teamWeeklyLaborCost)}
                  </div>
                </div>

                <div className="border border-gray-200 rounded p-3">
                  <div className="font-medium text-gray-900 mb-2">
                    Cost Split
                  </div>
                  <div className="text-gray-700">
                    Team labor total: $
                    {formatWholeNumber(simulationResult.teamLaborTotal)}
                  </div>
                  <div className="text-gray-700">
                    General costs total: $
                    {formatWholeNumber(simulationResult.generalCostsTotal)}
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">
                        Selected General Costs
                      </th>
                      <th className="px-3 py-2 text-left">
                        Recalculated Total ({defaultSystemCurrency})
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {simulationResult.generalCostBreakdown.map((row) => (
                      <tr key={`sim-gc-${row.id}`} className="border-t">
                        <td className="px-3 py-2 text-gray-800">{row.name}</td>
                        <td className="px-3 py-2 text-gray-900 font-medium">
                          ${formatWholeNumber(row.convertedTotal || 0)}
                        </td>
                      </tr>
                    ))}
                    {simulationResult.generalCostBreakdown.length === 0 && (
                      <tr>
                        <td
                          colSpan={2}
                          className="px-3 py-4 text-center text-gray-500"
                        >
                          No selected general costs.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );

  const renderPaintersCostTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white shadow overflow-hidden sm:rounded-md border border-slate-200">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 className="flex items-center gap-2 font-semibold text-slate-800">
                <span>Jobs</span>
                <InlineFieldTooltip
                  page="mobilization_costs_management"
                  section="painters_cost"
                  fieldKey="jobs_section"
                />
              </h3>
              <p className="text-xs text-slate-500">
                Yearly painter jobs with rate/hour, hours/week, and
                painting-time metadata.
              </p>
            </div>
            <button
              type="button"
              onClick={openCreateJobModal}
              className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2 text-sm"
            >
              <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
              Add Job
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 w-10"></th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Year
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Jobs
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {jobYears.map((group) => {
                  const isExpanded = expandedJobYears.has(group.year);
                  return (
                    <React.Fragment key={`jobs-year-${group.year}`}>
                      <tr className="bg-gray-50 hover:bg-gray-100">
                        <td className="px-3 py-3">
                          <button
                            type="button"
                            onClick={() => toggleJobYear(group.year)}
                            className="text-gray-500 hover:text-gray-700"
                            title={isExpanded ? "Collapse year" : "Expand year"}
                          >
                            <FontAwesomeIcon
                              icon={isExpanded ? faChevronDown : faChevronRight}
                            />
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                          {group.year}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {(group.jobs || []).length} jobs
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleCopyJobsFromYear(group.year)}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                            >
                              Copy
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteJobsYear(group.year)}
                              className="text-red-600 hover:text-red-900"
                              title={`Delete all jobs for ${group.year}`}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {isExpanded &&
                        (group.jobs || []).map((job) => {
                          const displayRatePerHour =
                            toNum(
                              job.convertedRatePerHour ||
                                job.converted_rate_per_hour,
                            ) || toNum(job.ratePerHour || job.rate_per_hour);
                          const paintingPct = toNum(
                            job.paintingTimePct || job.painting_time_pct || 100,
                          );
                          return (
                            <tr
                              key={`job-${job.id}`}
                              className="hover:bg-gray-50"
                            >
                              <td className="px-3 py-2"></td>
                              <td className="px-4 py-2 pl-8 text-sm font-medium text-gray-900">
                                {getJobDisplayName(job)}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-600">
                                ${formatMoney(displayRatePerHour)}
                                <span className="ml-1">
                                  {defaultSystemCurrency}
                                </span>
                                /hr,{" "}
                                {toNum(
                                  job.hoursPerWeek || job.hours_per_week,
                                ).toFixed(2)}
                                h/week, {formatMoney(paintingPct, 2)}% painting
                                time
                              </td>
                              <td className="px-4 py-2 text-sm font-medium whitespace-nowrap">
                                <button
                                  type="button"
                                  className="text-blue-600 hover:text-blue-900 mr-3"
                                  onClick={() =>
                                    openEditJobModal(job, group.year)
                                  }
                                  title="Edit job"
                                >
                                  <FontAwesomeIcon
                                    icon={faPencil}
                                    className="h-4 w-4"
                                  />
                                </button>
                                <button
                                  type="button"
                                  className="text-red-600 hover:text-red-900"
                                  onClick={() => handleDeleteJob(job.id)}
                                  title="Delete job"
                                >
                                  <FontAwesomeIcon
                                    icon={faTrash}
                                    className="h-4 w-4"
                                  />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white shadow overflow-hidden sm:rounded-md border border-slate-200">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 className="flex items-center gap-2 font-semibold text-slate-800">
                <span>Team Configuration</span>
                <InlineFieldTooltip
                  page="mobilization_costs_management"
                  section="painters_cost"
                  fieldKey="team_configuration_section"
                />
              </h3>
              <p className="text-xs text-slate-500">
                Yearly teams using jobs from the same year and member counts.
              </p>
            </div>
            <button
              type="button"
              onClick={openCreateTeamModal}
              className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2 text-sm"
            >
              <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
              Add Team
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 w-10"></th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Year
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Teams
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {teamYears.map((group) => {
                  const isExpanded = expandedTeamYears.has(group.year);
                  const teams = group.teams || [];

                  return (
                    <React.Fragment key={`teams-year-${group.year}`}>
                      <tr className="bg-gray-50 hover:bg-gray-100">
                        <td className="px-3 py-3">
                          <button
                            type="button"
                            onClick={() => toggleTeamYear(group.year)}
                            className="text-gray-500 hover:text-gray-700"
                            title={isExpanded ? "Collapse year" : "Expand year"}
                          >
                            <FontAwesomeIcon
                              icon={isExpanded ? faChevronDown : faChevronRight}
                            />
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                          {group.year}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {teams.length} teams
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleCopyTeamsFromYear(group.year)}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                            >
                              Copy
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteTeamsYear(group.year)}
                              className="text-red-600 hover:text-red-900"
                              title={`Delete all team configurations for ${group.year}`}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {isExpanded &&
                        teams.map((team) => {
                          const rows = sortTeamAllocationRows(
                            team.jobAllocations || team.job_allocations || [],
                          );
                          return (
                            <React.Fragment key={`team-config-${team.id}`}>
                              <tr className="hover:bg-gray-50 bg-white">
                                <td className="px-3 py-2"></td>
                                <td className="px-4 py-2 pl-8 text-sm font-medium text-gray-900">
                                  {getTeamDisplayName(team)}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-600">
                                  {rows.length} job rows
                                </td>
                                <td className="px-4 py-2 text-sm font-medium text-gray-900 whitespace-nowrap">
                                  <button
                                    type="button"
                                    className="text-blue-600 hover:text-blue-900 ml-3"
                                    onClick={() =>
                                      openEditTeamModal(team, group.year)
                                    }
                                    title="Edit team"
                                  >
                                    <FontAwesomeIcon
                                      icon={faPencil}
                                      className="h-4 w-4"
                                    />
                                  </button>
                                  <button
                                    type="button"
                                    className="text-red-600 hover:text-red-900 ml-3"
                                    onClick={() =>
                                      handleDeleteTeamConfig(team.id)
                                    }
                                    title="Delete team"
                                  >
                                    <FontAwesomeIcon
                                      icon={faTrash}
                                      className="h-4 w-4"
                                    />
                                  </button>
                                </td>
                              </tr>
                              {rows.map((row) => (
                                <tr
                                  key={`team-row-${row.id}`}
                                  className="hover:bg-gray-50"
                                >
                                  <td className="px-3 py-2"></td>
                                  <td className="px-4 py-2 pl-12 text-sm text-gray-900">
                                    {getJobDisplayName(row.job) || "-"}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-600">
                                    {toInt(row.memberCount || row.member_count)}{" "}
                                    members
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-600"></td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

  const renderProductivityTab = () => (
    <div className="space-y-4">
      <div className="bg-white border rounded p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900">
            <span>Painter Productivity Matrix</span>
            <InlineFieldTooltip
              page="mobilization_costs_management"
              section="painter_productivity"
              fieldKey="productivity_matrix_section"
            />
          </h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openCreateProductivityModal}
              className="px-3 py-1.5 rounded bg-slate-700 text-white hover:bg-slate-800 text-sm"
            >
              Add Productivity Row
            </button>
          </div>
        </div>

        <div className="overflow-x-auto border rounded">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-3 py-2 w-10"></th>
                <th className="text-left px-3 py-2">Year</th>
                <th className="text-left px-3 py-2">Steel Size</th>
                <th className="text-left px-3 py-2">Coat Type</th>
                <th className="text-left px-3 py-2">Speed</th>
                <th className="text-left px-3 py-2">
                  {productivityApplicationTypeHeaderLabel} (sqft/hr)
                </th>
                <th className="text-left px-3 py-2">
                  {productivityApplicationTypeHeaderLabel} (sqm/hr)
                </th>
                <th className="text-left px-3 py-2">
                  {productivityApplicationTypeHeaderLabel} (manhour/sqm)
                </th>
                <th className="text-left px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {productivityMatrixGrouped.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    className="px-4 py-6 text-center text-gray-500"
                  >
                    No productivity data yet.
                  </td>
                </tr>
              )}

              {productivityMatrixGrouped.map((group) => {
                const isExpanded = expandedProductivityYears.has(group.year);
                return (
                  <React.Fragment key={`prod-year-${group.year}`}>
                    <tr className="bg-gray-50 hover:bg-gray-100">
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => toggleProductivityYear(group.year)}
                          className="text-gray-500 hover:text-gray-700"
                          title={isExpanded ? "Collapse year" : "Expand year"}
                        >
                          <FontAwesomeIcon
                            icon={isExpanded ? faChevronDown : faChevronRight}
                          />
                        </button>
                      </td>
                      <td className="px-3 py-2 text-sm font-semibold text-gray-900">
                        {group.year}
                      </td>
                      <td
                        colSpan={6}
                        className="px-3 py-2 text-sm text-gray-600"
                      >
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                          <span>{group.rows.length} grouped rows</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              handleCopyProductivityFromYear(group.year)
                            }
                            className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                          >
                            Copy
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handleDeleteProductivityYear(group.year)
                            }
                            className="text-red-600 hover:text-red-900"
                            title={`Delete all productivity rows for ${group.year}`}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {isExpanded &&
                      group.rows.map((row) => {
                        const methodKeysForRow = Array.from(
                          new Set(productivityApplicationTypeKeys),
                        ).filter(Boolean);
                        const compactSqft = methodKeysForRow
                          .map((methodKey) =>
                            formatMoney(toNum(row.sqft?.[methodKey]), 2),
                          )
                          .join(" / ");
                        const compactSqm = methodKeysForRow
                          .map((methodKey) =>
                            formatMoney(
                              toNum(row.sqft?.[methodKey]) * SQFT_TO_SQM,
                              4,
                            ),
                          )
                          .join(" / ");
                        const compactMh = methodKeysForRow
                          .map((methodKey) => {
                            const sqm =
                              toNum(row.sqft?.[methodKey]) * SQFT_TO_SQM;
                            const mh = sqm > 0 ? 1 / sqm : 0;
                            return sqm > 0 ? formatMoney(mh, 4) : "-";
                          })
                          .join(" / ");

                        return (
                          <tr
                            key={row.key}
                            className="hover:bg-gray-50 bg-white"
                          >
                            <td className="px-3 py-2"></td>
                            <td className="px-3 py-2 text-gray-700">
                              {row.year}
                            </td>
                            <td className="px-3 py-2 text-gray-700">
                              {row.steel_size}
                            </td>
                            <td className="px-3 py-2 text-gray-700">
                              {row.coat_type}
                            </td>
                            <td className="px-3 py-2 text-gray-700">
                              {row.speed_band}
                            </td>

                            <td className="px-3 py-2 text-gray-900">
                              <div>{compactSqft}</div>
                            </td>
                            <td className="px-3 py-2 text-gray-700">
                              <div>{compactSqm}</div>
                            </td>
                            <td className="px-3 py-2 text-gray-700">
                              <div>{compactMh}</div>
                            </td>

                            <td className="px-3 py-2 whitespace-nowrap">
                              <button
                                type="button"
                                className="text-blue-600 hover:text-blue-900"
                                onClick={() => openEditProductivityModal(row)}
                                title="Edit row"
                              >
                                <FontAwesomeIcon
                                  icon={faPencil}
                                  className="h-4 w-4"
                                />
                              </button>
                              <button
                                type="button"
                                className="text-red-600 hover:text-red-900 ml-3"
                                onClick={() => handleDeleteProductivity(row)}
                                title="Delete row"
                              >
                                <FontAwesomeIcon
                                  icon={faTrash}
                                  className="h-4 w-4"
                                />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderProductivityAdjustmentsTab = () => (
    <div className="grid grid-cols-1 lg:grid-cols-[15rem_26rem_minmax(20rem,1fr)] gap-4 items-start">
      <div className="w-full bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900">
              Access Methods
            </h3>
            <InlineFieldTooltip
              page="mobilization_costs_management"
              section="productivity_adjustments"
              fieldKey="access_methods_panel"
              fallbackText="Manage access methods used by painter productivity adjustments."
              title="Access Methods"
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handlePruneOrphanMethods}
              disabled={pruningOrphanMethods}
              className="text-xs text-gray-500 hover:text-gray-800 disabled:opacity-50"
              title="Remove references to deleted methods from all bridges and re-normalize their splits"
            >
              {pruningOrphanMethods ? "Cleaning…" : "Clean bridges"}
            </button>
            <button
              type="button"
              onClick={openCreateAccessMethodModal}
              className="text-blue-600 hover:text-blue-800"
              title="Add method"
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
        </div>

        <div className="max-h-[560px] overflow-y-auto">
          <button
            type="button"
            onClick={() => setSelectedProductivityMethodFilter("all")}
            className={`w-full flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100 text-left ${
              selectedProductivityMethodFilter === "all"
                ? "bg-blue-50 text-blue-800"
                : "hover:bg-gray-50 text-gray-900"
            } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset`}
          >
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">All methods</div>
              <div className="text-xs text-gray-500">
                {productivityMethodAdjustmentRows.length} records
              </div>
            </div>
          </button>
          {sortedAccessMethodRows.length === 0 ? (
            <div className="px-4 py-6 text-sm text-gray-500">
              No access methods yet. Use + to add one.
            </div>
          ) : (
            sortedAccessMethodRows.map((method) => (
              <div
                key={method.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedProductivityMethodFilter(method.id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setSelectedProductivityMethodFilter(method.id);
                  }
                }}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100 text-left ${
                  String(selectedProductivityMethodFilter) === String(method.id)
                    ? "bg-blue-50 text-blue-800"
                    : "hover:bg-gray-50 text-gray-900"
                } cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset`}
              >
                <div className="min-w-0">
                  <div
                    className={`truncate text-sm font-medium ${
                      String(selectedProductivityMethodFilter) ===
                      String(method.id)
                        ? "text-blue-900"
                        : "text-gray-900"
                    }`}
                  >
                    {method.value}
                  </div>
                  <div className="text-xs text-gray-500">
                    {productivityMethodAdjustmentCountByMethodId.get(
                      String(method.id),
                    ) || 0} records
                  </div>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      openEditAccessMethodModal(method);
                    }}
                    className="text-blue-600 hover:text-blue-900"
                    title="Edit method"
                  >
                    <FontAwesomeIcon icon={faPencil} />
                  </button>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleDeleteAccessMethod(method);
                    }}
                    className="text-red-600 hover:text-red-900"
                    title="Delete method"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="w-full bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-3 py-3 border-b border-gray-200 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900 text-sm leading-tight">
                Damage Combinations & Access Methods
              </h3>
              <InlineFieldTooltip
                page="mobilization_costs_management"
                section="productivity_adjustments"
                fieldKey="damage_method_matrix_panel"
                fallbackText="Set productivity adjustment percentages for each generated damage combination and access method."
                title="Productivity Adjustment Matrix"
              />
            </div>
            <div className="mt-2 inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              {selectedProductivityMethodFilterLabel} - {visibleProductivityMethodAdjustmentRows.length} records
            </div>
          </div>
          <button
            type="button"
            onClick={handleSaveProductivityMethodAdjustments}
            disabled={
              savingProductivityMethodAdjustments ||
              loadingProductivityMethodAdjustments ||
              productivityMethodAdjustmentRows.length === 0
            }
            className="flex-none px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {savingProductivityMethodAdjustments ? "Saving..." : "Save"}
          </button>
        </div>

        {productivityMethodAdjustmentError && (
          <div className="m-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {productivityMethodAdjustmentError}
          </div>
        )}

        <div className="overflow-x-auto">
          {loadingProductivityMethodAdjustments ? (
            <div className="p-6 text-center text-gray-500">
              Loading productivity adjustments...
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Class
                  </th>
                  {productivityAdjustmentGrid.severities.map((severity) => (
                    <th
                      key={`severity-${severity.code}`}
                      className="px-1.5 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                      title={severity.name || severity.code}
                    >
                      {severity.code}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white">
                {productivityAdjustmentGrid.groups.map((group) => (
                  <React.Fragment key={`method-${group.methodId}`}>
                    <tr>
                      <td
                        colSpan={
                          productivityAdjustmentGrid.severities.length + 1
                        }
                        className="border-y border-gray-200 bg-gray-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-700"
                      >
                        {group.methodName}
                      </td>
                    </tr>
                    {group.rows.map((classRow) => (
                      <tr
                        key={`${group.methodId}-${classRow.classCode}`}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td
                          className="px-3 py-1 whitespace-nowrap font-medium text-gray-900"
                          title={classRow.className || classRow.classCode}
                        >
                          {classRow.classCode}
                        </td>
                        {classRow.cells.map((cell) => {
                          if (!cell.row) {
                            return (
                              <td
                                key={`${group.methodId}-${classRow.classCode}-${cell.severityCode}`}
                                className="px-1.5 py-1 text-center text-gray-300"
                              >
                                —
                              </td>
                            );
                          }
                          const isAdjusted =
                            Number(cell.row.productivityPercentage) !== 100;
                          return (
                            <td
                              key={cell.row.adjustmentKey}
                              className="px-1.5 py-1 text-center"
                            >
                              <input
                                type="text"
                                inputMode="decimal"
                                value={cell.row.productivityPercentage}
                                onChange={(event) =>
                                  updateProductivityMethodAdjustmentPercent(
                                    cell.row,
                                    event.target.value,
                                  )
                                }
                                onBlur={() =>
                                  commitProductivityMethodAdjustmentPercent(
                                    cell.row,
                                  )
                                }
                                onFocus={(event) => event.target.select()}
                                title={`${cell.row.combinationCode} - ${cell.row.accessMethodName}`}
                                aria-label={`${group.methodName} productivity for ${cell.row.combinationCode}`}
                                className={`w-full min-w-[3.5rem] px-1.5 py-1 border rounded text-center ${
                                  isAdjusted
                                    ? "border-blue-300 bg-blue-50 font-semibold text-blue-800"
                                    : "border-gray-300 text-gray-700"
                                }`}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                {productivityAdjustmentGrid.groups.length === 0 && (
                  <tr>
                    <td
                      colSpan={productivityAdjustmentGrid.severities.length + 1}
                      className="px-4 py-6 text-center text-sm text-gray-500"
                    >
                      {productivityMethodAdjustmentRows.length === 0
                        ? "Add at least one Access Method to generate adjustment rows."
                        : "No adjustment rows for the selected method."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {productivityAdjustmentGrid.groups.length > 0 && (
          <div className="border-t border-gray-200 px-3 py-2 text-xs text-gray-500">
            Productivity %, whole or decimal, from {MIN_ADJUSTMENT_PERCENT} to{" "}
            {MAX_ADJUSTMENT_PERCENT}. 100 = no effect;{" "}
            <span className="font-semibold text-blue-800">highlighted</span>{" "}
            cells are adjusted.
          </div>
        )}
      </div>

      <div className="w-full bg-white rounded-lg border border-blue-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-blue-100 bg-blue-50">
          <h3 className="text-sm font-semibold text-blue-950">
            CSV Setup Required
          </h3>
        </div>
        <div className="p-4 space-y-4 text-sm text-gray-700">
          <div>
            <p className="font-medium text-gray-900">Workflow</p>
            <ol className="mt-2 list-decimal list-inside space-y-1">
              <li>Download area data from Bridge Tools.</li>
              <li>Fill the CSV column named <span className="font-mono text-xs">access_methods</span>.</li>
              <li>Upload the edited CSV before running simulation or repair project costing.</li>
            </ol>
          </div>

          <div>
            <p className="font-medium text-gray-900">Valid values</p>
            <div className="mt-2 rounded border border-green-200 bg-green-50 p-3 font-mono text-xs text-green-900 space-y-1">
              <div>Swing</div>
              <div>Swing-40;Chair-25;Robot-35</div>
              <div>Robot-100</div>
            </div>
            <p className="mt-2 text-xs text-gray-600">
              A single method such as <span className="font-mono">Swing</span> means <span className="font-mono">Swing-100</span>. Multi-method rows must total 100%.
            </p>
          </div>

          <div>
            <p className="font-medium text-gray-900">Invalid examples</p>
            <div className="mt-2 rounded border border-red-200 bg-red-50 p-3 font-mono text-xs text-red-900 space-y-1">
              <div>Swing-40;Chair-25;Robot-30</div>
              <div>Swing-40,Chair-60</div>
              <div>Swnig-100</div>
            </div>
            <p className="mt-2 text-xs text-gray-600">
              Invalid totals, comma separators, or misspelled method names are rejected during CSV import.
            </p>
          </div>

          <div className="rounded border border-amber-200 bg-amber-50 p-3 text-amber-900">
            Productivity adjustments on this tab are applied only when an area-data row has a valid <span className="font-mono text-xs">access_methods</span> value. Blank CSV rows do not receive these method adjustments.
          </div>
        </div>
      </div>
    </div>
  );

  const renderGeneralCostsTab = () => (
    <div className="space-y-4">
      <div className="bg-white border rounded p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900">
            <span>General Costs</span>
            <InlineFieldTooltip
              page="mobilization_costs_management"
              section="general_costs"
              fieldKey="general_costs_section"
            />
          </h3>
          <button
            type="button"
            onClick={openCreateGeneralCostModal}
            className="px-3 py-1.5 rounded bg-slate-700 text-white hover:bg-slate-800 text-sm"
          >
            Add General Cost
          </button>
        </div>

        <div className="overflow-x-auto border rounded">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-3 py-2 w-10"></th>
                <th className="text-left px-3 py-2">Year</th>
                <th className="text-left px-3 py-2">Cost Name</th>
                <th className="text-left px-3 py-2">Formula</th>
                <th className="text-left px-3 py-2">Confined Space</th>
                <th className="text-left px-3 py-2">Currency</th>
                <th className="text-left px-3 py-2">Exposure Class(es)</th>
                <th className="text-left px-3 py-2">
                  Total ({defaultSystemCurrency})
                </th>
                <th className="text-left px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {generalCostYears.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    className="px-4 py-6 text-center text-gray-500"
                  >
                    No general costs yet.
                  </td>
                </tr>
              )}

              {generalCostYears.map((group) => {
                const isExpanded = expandedGeneralCostYears.has(group.year);
                return (
                  <React.Fragment key={`general-cost-year-${group.year}`}>
                    <tr className="bg-gray-50 hover:bg-gray-100">
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => toggleGeneralCostYear(group.year)}
                          className="text-gray-500 hover:text-gray-700"
                          title={isExpanded ? "Collapse year" : "Expand year"}
                        >
                          <FontAwesomeIcon
                            icon={isExpanded ? faChevronDown : faChevronRight}
                          />
                        </button>
                      </td>
                      <td className="px-3 py-2 text-sm font-semibold text-gray-900">
                        {group.year}
                      </td>
                      <td
                        colSpan={6}
                        className="px-3 py-2 text-sm text-gray-600"
                      >
                        {(group.costs || []).length} cost rows
                      </td>
                      <td className="px-3 py-2 text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              handleCopyGeneralCostsFromYear(group.year)
                            }
                            className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                          >
                            Copy
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handleDeleteGeneralCostsYear(group.year)
                            }
                            className="text-red-600 hover:text-red-900"
                            title={`Delete all general cost rows for ${group.year}`}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {isExpanded &&
                      (group.costs || []).map((row) => {
                        const exposureIds = normalizeExposureIds(
                          row.exposureClassIds || row.exposure_class_ids || [],
                        );
                        const exposureLabel =
                          exposureIds.length > 0
                            ? exposureIds
                                .map(
                                  (id) => exposureCodeById.get(id) || `#${id}`,
                                )
                                .join(", ")
                            : "All Exposure Classes";

                        const formulaLabel =
                          GENERAL_COST_CALC_TYPES.find(
                            (t) =>
                              t.id ===
                              (row.calculationType || row.calculation_type),
                          )?.label || "Quantity x Rate";

                        const convertedTotal = calculateGeneralCostTotal(
                          row,
                          true,
                        );

                        return (
                          <tr
                            key={`general-cost-row-${row.id}`}
                            className="hover:bg-gray-50 bg-white"
                          >
                            <td className="px-3 py-2"></td>
                            <td className="px-3 py-2 text-gray-700">
                              {row.year}
                            </td>
                            <td className="px-3 py-2 text-gray-900">
                              {row.costCategory || row.cost_category}
                              {(row.description || "").trim() && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {row.description}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-gray-700">
                              <div>{formulaLabel}</div>
                              <div className="text-xs text-gray-500 mt-1">
                                {buildGeneralCostFormulaPreview(row)}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-gray-700">
                              {Boolean(row.confinedSpace || row.confined_space)
                                ? "Yes"
                                : "No"}
                            </td>
                            <td className="px-3 py-2 text-gray-700">
                              {row.currency || defaultSystemCurrency}
                            </td>
                            <td className="px-3 py-2 text-gray-700">
                              {exposureLabel}
                            </td>
                            <td className="px-3 py-2 text-gray-900 font-medium">
                              {convertedTotal === null
                                ? "Conversion missing"
                                : `$${formatMoney(convertedTotal)}`}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <button
                                type="button"
                                className="text-blue-600 hover:text-blue-900"
                                onClick={() =>
                                  openEditGeneralCostModal(row, group.year)
                                }
                                title="Edit row"
                              >
                                <FontAwesomeIcon
                                  icon={faPencil}
                                  className="h-4 w-4"
                                />
                              </button>
                              <button
                                type="button"
                                className="text-red-600 hover:text-red-900 ml-3"
                                onClick={() => handleDeleteGeneralCost(row.id)}
                                title="Delete row"
                              >
                                <FontAwesomeIcon
                                  icon={faTrash}
                                  className="h-4 w-4"
                                />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    if (activeTab === "painters-cost") return renderPaintersCostTab();
    if (activeTab === "general-costs") {
      return renderGeneralCostsTab();
    }
    if (activeTab === "painter-productivity") {
      return renderProductivityTab();
    }
    if (activeTab === "productivity-adjustments") {
      return renderProductivityAdjustmentsTab();
    }
    if (activeTab === "mobilization-costs") {
      return renderMobilizationCostsTab();
    }
    return null;
  };

  if (loading) {
    return (
      <div className="py-6 text-gray-600">Loading Mobilization Master...</div>
    );
  }

  return (
    <div className="py-6 space-y-6">
      <div className="flex items-start gap-2">
        <p className="text-gray-600">
          Manage man-hour cost, productivity, general costs, and standalone
          mobilization cost simulations.
        </p>
        <InlineFieldTooltip
          page="mobilization_costs"
          section="page_description"
          fieldKey="mobilization_costs_overview"
        />
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 border border-red-200 rounded p-3">
          {error}
        </div>
      )}

      <div className="mb-2">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
                onClick={() => {
                  setActiveTab(tab.id);
                  setSearchParams({ tab: tab.id });
                }}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {renderTabContent()}

      {showAccessMethodModal && (
        <div className="fixed inset-0 z-[90] bg-gray-600 bg-opacity-50 overflow-y-auto p-4">
          <div className="relative mx-auto mt-10 w-full max-w-md bg-white rounded shadow-lg border">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <span>
                  {editingAccessMethod
                    ? "Edit Access Method"
                    : "Add Access Method"}
                </span>
                <InlineFieldTooltip
                  page="mobilization_costs_management"
                  section="productivity_adjustments"
                  fieldKey="access_method_form"
                  fallbackText="Create or edit a method name in the Access Methods system list."
                  title="Access Method Form"
                />
              </h3>
              <button
                type="button"
                onClick={closeAccessMethodModal}
                className="text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>

            <div className="p-6 space-y-4">
              {accessMethodError && (
                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {accessMethodError}
                </div>
              )}

              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Method Name
                </label>
                <input
                  type="text"
                  value={accessMethodForm.value}
                  onChange={(event) =>
                    setAccessMethodForm((prev) => ({
                      ...prev,
                      value: event.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Sort Order
                </label>
                <input
                  type="number"
                  step="1"
                  value={accessMethodForm.sort_order}
                  onChange={(event) =>
                    setAccessMethodForm((prev) => ({
                      ...prev,
                      sort_order: event.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t px-6 py-4">
              <button
                type="button"
                onClick={closeAccessMethodModal}
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveAccessMethod}
                disabled={savingAccessMethod}
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {savingAccessMethod ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showJobModal && (
        <div className="fixed inset-0 z-[90] bg-gray-600 bg-opacity-50 overflow-y-auto p-4">
          <div className="relative mx-auto mt-10 w-full max-w-2xl bg-white rounded shadow-lg border">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <span>{editingJobId ? "Edit Job" : "Add Job"}</span>
                <InlineFieldTooltip
                  page="mobilization_costs"
                  section="modal_titles"
                  fieldKey="job_form"
                  fallbackText="Create or edit a mobilization job row."
                  title="Job Form"
                />
              </h3>
              <button
                type="button"
                onClick={closeJobModal}
                className="text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Year</label>
                <select
                  value={jobForm.year}
                  onChange={(e) =>
                    setJobForm((prev) => ({ ...prev, year: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                >
                  {modalYearOptions.map((year) => (
                    <option key={`job-year-${year}`} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Job Name
                </label>
                <input
                  type="text"
                  value={jobForm.job_name}
                  onChange={(e) =>
                    setJobForm((prev) => ({
                      ...prev,
                      job_name: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Job Identifier
                </label>
                <input
                  type="text"
                  value={jobForm.job_identifier}
                  onChange={(e) =>
                    setJobForm((prev) => ({
                      ...prev,
                      job_identifier: sanitizeJobIdentifierInput(
                        e.target.value,
                      ),
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded uppercase"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Rate/Hour
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={jobForm.rate_per_hour}
                  onChange={(e) =>
                    setJobForm((prev) => ({
                      ...prev,
                      rate_per_hour: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Currency
                </label>
                <select
                  value={jobForm.currency}
                  onChange={(e) =>
                    setJobForm((prev) => ({
                      ...prev,
                      currency: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                >
                  <option value="CAD">CAD</option>
                  <option value="USD">USD</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Hours/Week
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={jobForm.hours_per_week}
                  onChange={(e) =>
                    setJobForm((prev) => ({
                      ...prev,
                      hours_per_week: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Painting Time Used (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={jobForm.painting_time_pct}
                  onChange={(e) =>
                    setJobForm((prev) => ({
                      ...prev,
                      painting_time_pct: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Sort Order
                </label>
                <input
                  type="number"
                  step="1"
                  value={jobForm.sort_order}
                  onChange={(e) =>
                    setJobForm((prev) => ({
                      ...prev,
                      sort_order: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Converted Rate ({defaultSystemCurrency}/hr)
                </label>
                <div className="w-full px-3 py-2 border border-gray-200 rounded bg-gray-50 text-gray-700">
                  {convertingJobRate
                    ? "Converting..."
                    : `$${formatMoney(jobForm.converted_rate_per_hour || 0)}`}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t px-6 py-4">
              <button
                type="button"
                onClick={closeJobModal}
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveJob}
                disabled={savingJob}
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {savingJob
                  ? "Saving..."
                  : editingJobId
                    ? "Update Job"
                    : "Save Job"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTeamModal && (
        <div className="fixed inset-0 z-[90] bg-gray-600 bg-opacity-50 overflow-y-auto p-4">
          <div className="relative mx-auto mt-10 w-full max-w-5xl bg-white rounded shadow-lg border">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <span>
                  {editingTeamId
                    ? "Edit Team Configuration"
                    : "Add Team Configuration"}
                </span>
                <InlineFieldTooltip
                  page="mobilization_costs"
                  section="modal_titles"
                  fieldKey="team_configuration_form"
                  fallbackText="Create or edit a team configuration and job allocations."
                  title="Team Configuration Form"
                />
              </h3>
              <button
                type="button"
                onClick={closeTeamModal}
                className="text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">
                    Year
                  </label>
                  <select
                    value={teamForm.year}
                    onChange={(e) =>
                      setTeamForm((prev) => ({ ...prev, year: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  >
                    {modalYearOptions.map((year) => (
                      <option key={`team-year-${year}`} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">
                    Sort Order
                  </label>
                  <input
                    type="number"
                    step="1"
                    value={teamForm.sort_order}
                    onChange={(e) =>
                      setTeamForm((prev) => ({
                        ...prev,
                        sort_order: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">
                    Team Name
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-200 rounded bg-gray-50 text-gray-700 min-h-[42px] flex items-center">
                    {generatedTeamName || "Generated from sorted job rows"}
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-900">
                    Team Jobs
                  </h4>
                  <button
                    type="button"
                    onClick={addAllocationRow}
                    className="px-3 py-1.5 rounded bg-slate-700 text-white hover:bg-slate-800 text-sm"
                  >
                    Add Job Row
                  </button>
                </div>

                <div className="overflow-x-auto border rounded">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-2">Job</th>
                        <th className="text-left px-3 py-2">
                          Rate/Hour ({defaultSystemCurrency})
                        </th>
                        <th className="text-left px-3 py-2">Hours/Week</th>
                        <th className="text-left px-3 py-2">Team Members</th>
                        <th className="text-left px-3 py-2">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamForm.allocations.map((row, index) => {
                        const selectedJob = jobsForSelectedTeamYear.find(
                          (job) => String(job.id) === String(row.job_id),
                        );
                        const rate = toNum(
                          selectedJob?.convertedRatePerHour ||
                            selectedJob?.converted_rate_per_hour ||
                            selectedJob?.ratePerHour ||
                            selectedJob?.rate_per_hour ||
                            0,
                        );
                        const hpw = toNum(
                          selectedJob?.hoursPerWeek ||
                            selectedJob?.hours_per_week ||
                            0,
                        );

                        return (
                          <tr key={`alloc-${index}`} className="border-t">
                            <td className="px-3 py-2">
                              <select
                                value={row.job_id}
                                onChange={(e) =>
                                  updateAllocationField(
                                    index,
                                    "job_id",
                                    e.target.value,
                                  )
                                }
                                className="w-full px-2 py-1 border border-gray-300 rounded"
                              >
                                <option value="">Select Job</option>
                                {jobsForSelectedTeamYear.map((job) => (
                                  <option
                                    key={`job-opt-${job.id}`}
                                    value={job.id}
                                  >
                                    {getJobDisplayName(job)}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-3 py-2 text-gray-700">
                              ${formatMoney(rate)}
                            </td>
                            <td className="px-3 py-2 text-gray-700">
                              {hpw.toFixed(2)}
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                step="1"
                                min="1"
                                value={row.member_count}
                                onChange={(e) =>
                                  updateAllocationField(
                                    index,
                                    "member_count",
                                    e.target.value,
                                  )
                                }
                                className="w-24 px-2 py-1 border border-gray-300 rounded"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <button
                                type="button"
                                onClick={() => removeAllocationRow(index)}
                                className="text-red-600 hover:text-red-900"
                                title="Remove row"
                              >
                                <FontAwesomeIcon
                                  icon={faTrash}
                                  className="h-4 w-4"
                                />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t px-6 py-4">
              <button
                type="button"
                onClick={closeTeamModal}
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveTeam}
                disabled={savingTeam}
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {savingTeam
                  ? "Saving..."
                  : editingTeamId
                    ? "Update Team"
                    : "Save Team"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showProductivityModal && (
        <div className="fixed inset-0 z-[90] bg-gray-600 bg-opacity-50 overflow-y-auto p-4">
          <div className="relative mx-auto mt-10 w-full max-w-3xl bg-white rounded shadow-lg border">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <span>
                  {editingProductivityKey
                    ? "Edit Painter Productivity"
                    : "Add Painter Productivity"}
                </span>
                <InlineFieldTooltip
                  page="mobilization_costs"
                  section="modal_titles"
                  fieldKey="painter_productivity_form"
                  fallbackText="Create or edit painter productivity rows by speed band and coat type."
                  title="Painter Productivity Form"
                />
              </h3>
              <button
                type="button"
                onClick={closeProductivityModal}
                className="text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">
                    Year
                  </label>
                  <select
                    value={productivityForm.year}
                    onChange={(e) =>
                      setProductivityForm((prev) => ({
                        ...prev,
                        year: Number(e.target.value),
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  >
                    {modalYearOptions.map((year) => (
                      <option key={`prod-year-${year}`} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-1">
                    Steel Size
                  </label>
                  <select
                    value={productivityForm.steel_size}
                    onChange={(e) =>
                      setProductivityForm((prev) => ({
                        ...prev,
                        steel_size: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  >
                    <option value="HSS">HSS</option>
                    <option value="LSS">LSS</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-1">
                    Coat Type
                  </label>
                  <SearchableAddDropdown
                    value={productivityForm.coat_type}
                    options={mergedCoatTypeOptions}
                    onChange={(nextValue) =>
                      setProductivityForm((prev) => ({
                        ...prev,
                        coat_type: nextValue,
                      }))
                    }
                    onAddOption={handleAddCoatTypeOption}
                    placeholder="Select coat type"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-1">
                    Speed Bands
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-200 rounded bg-gray-50 text-gray-700">
                    Slow, Medium, Fast
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {SPEED_BANDS.map((speed) => {
                  const band =
                    productivityForm.speed_rows?.[speed] ||
                    createEmptySpeedSqftRows(productivityApplicationTypeKeys)[
                      speed
                    ];
                  return (
                    <div
                      key={`speed-input-${speed}`}
                      className="border border-gray-200 rounded p-3"
                    >
                      <div className="text-sm font-medium text-gray-800 mb-3">
                        {speed}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {productivityApplicationTypeDescriptors.map((method) => (
                          <div key={`speed-${speed}-method-${method.key}`}>
                            <label className="block text-sm text-gray-700 mb-1">
                              {method.label} (sqft/hr)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={band[method.key] || ""}
                              onChange={(e) =>
                                setProductivityForm((prev) => ({
                                  ...prev,
                                  speed_rows: {
                                    ...(prev.speed_rows ||
                                      createEmptySpeedSqftRows(
                                        productivityApplicationTypeKeys,
                                      )),
                                    [speed]: {
                                      ...(prev.speed_rows?.[speed] ||
                                        createEmptySpeedSqftRows(
                                          productivityApplicationTypeKeys,
                                        )[speed]),
                                      [method.key]: e.target.value,
                                    },
                                  },
                                }))
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t px-6 py-4">
              <button
                type="button"
                onClick={closeProductivityModal}
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveProductivity}
                disabled={savingProductivity}
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {savingProductivity
                  ? "Saving..."
                  : editingProductivityKey
                    ? "Update Row"
                    : "Save Row"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showGeneralCostModal && (
        <div className="fixed inset-0 z-[90] bg-gray-600 bg-opacity-50 overflow-y-auto p-4">
          <div className="relative mx-auto mt-10 w-full max-w-4xl bg-white rounded shadow-lg border">
            <GeneralCostEditorForm
              title={
                editingGeneralCostId ? "Edit General Cost" : "Add General Cost"
              }
              formData={generalCostForm}
              setFormData={setGeneralCostForm}
              yearOptions={modalYearOptions}
              exposureClassOptions={exposureClassOptions}
              exposureCodeById={exposureCodeById}
              defaultSystemCurrency={defaultSystemCurrency}
              saving={savingGeneralCost}
              isEditing={Boolean(editingGeneralCostId)}
              onClose={closeGeneralCostModal}
              onSave={handleSaveGeneralCost}
              calculateGeneralCostTotal={calculateGeneralCostTotal}
            />
          </div>
        </div>
      )}

      {showMobilizationConfigModal && (
        <div className="fixed inset-0 z-[90] bg-gray-600 bg-opacity-50 overflow-y-auto p-4">
          <div className="relative mx-auto mt-10 w-full max-w-4xl bg-white rounded shadow-lg border">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <span>
                  {editingMobilizationConfigId
                    ? "Edit Mobilization Cost"
                    : "Add New Configuration"}
                </span>
                <InlineFieldTooltip
                  page="mobilization_costs"
                  section="modal_titles"
                  fieldKey="mobilization_cost_form"
                  fallbackText="Create or edit mobilization cost simulation inputs."
                  title="Mobilization Cost Form"
                />
              </h3>
              <button
                type="button"
                onClick={closeMobilizationConfigModal}
                className="text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Year</label>
                <select
                  value={mobilizationConfigForm.year}
                  onChange={(e) =>
                    setMobilizationConfigForm((prev) => {
                      const nextYear = Number(e.target.value);
                      return {
                        ...prev,
                        year: nextYear,
                        team_id: "",
                        selected_general_cost_ids:
                          defaultGeneralCostIdsForYear(nextYear),
                      };
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                >
                  {modalYearOptions.map((year) => (
                    <option key={`mobilization-year-${year}`} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-1">Team</label>
                <select
                  value={mobilizationConfigForm.team_id}
                  onChange={(e) =>
                    setMobilizationConfigForm((prev) => ({
                      ...prev,
                      team_id: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                >
                  <option value="">Select Team</option>
                  {eligibleTeamsForMobilizationForm.map((team) => (
                    <option
                      key={`mobilization-team-${team.id}`}
                      value={team.id}
                    >
                      {team.label}
                    </option>
                  ))}
                </select>
                {eligibleTeamsForMobilizationForm.length === 0 && (
                  <p className="mt-1 text-xs text-red-600">
                    No teams found for selected year or previous years.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Paint Speed
                </label>
                <select
                  value={mobilizationConfigForm.speed_band}
                  onChange={(e) =>
                    setMobilizationConfigForm((prev) => ({
                      ...prev,
                      speed_band: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                >
                  {SPEED_BANDS.map((band) => (
                    <option key={`mobilization-speed-${band}`} value={band}>
                      {band}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Sort Order
                </label>
                <input
                  type="number"
                  step="1"
                  value={mobilizationConfigForm.sort_order}
                  onChange={(e) =>
                    setMobilizationConfigForm((prev) => ({
                      ...prev,
                      sort_order: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Active for Year
                </label>
                <div className="inline-flex items-center gap-3 text-sm text-gray-700 mt-2">
                  <button
                    type="button"
                    onClick={() =>
                      setMobilizationConfigForm((prev) => ({
                        ...prev,
                        is_active: !Boolean(prev.is_active),
                      }))
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      Boolean(mobilizationConfigForm.is_active)
                        ? "bg-blue-600"
                        : "bg-gray-300"
                    }`}
                    title={
                      Boolean(mobilizationConfigForm.is_active)
                        ? "Deactivate"
                        : "Activate"
                    }
                    aria-label="Toggle active for selected year"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        Boolean(mobilizationConfigForm.is_active)
                          ? "translate-x-6"
                          : "translate-x-1"
                      }`}
                    />
                  </button>
                  <span>Set this row as active for selected year</span>
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm text-gray-700 mb-2">
                  General Costs Selection
                </label>
                <div className="border border-gray-300 rounded p-3">
                  <div className="text-xs text-gray-600 mb-3">
                    {generalCostsForMobilizationForm.sourceYear
                      ? `Using General Costs from year ${generalCostsForMobilizationForm.sourceYear} (same or nearest previous year).`
                      : "No General Costs found for selected year or previous years."}
                  </div>

                  {generalCostsForMobilizationForm.costs.length > 0 ? (
                    <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                      {generalCostsForMobilizationForm.costs.map((cost) => {
                        const costId = Number(cost.id);
                        const selected = (
                          mobilizationConfigForm.selected_general_cost_ids || []
                        ).includes(costId);
                        return (
                          <div
                            key={`mobilization-general-cost-${cost.id}`}
                            className="flex items-center justify-between border border-gray-200 rounded px-3 py-2"
                          >
                            <div className="min-w-0">
                              <div className="text-sm text-gray-900 truncate">
                                {cost.costCategory || cost.cost_category}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {buildGeneralCostFormulaPreview(cost)}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setMobilizationConfigForm((prev) => {
                                  const next = new Set(
                                    prev.selected_general_cost_ids || [],
                                  );
                                  if (selected) next.delete(costId);
                                  else next.add(costId);
                                  return {
                                    ...prev,
                                    selected_general_cost_ids: Array.from(next),
                                  };
                                })
                              }
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                selected ? "bg-blue-600" : "bg-gray-300"
                              }`}
                              title={selected ? "Deselect" : "Select"}
                              aria-label={`Toggle general cost ${costId}`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  selected ? "translate-x-6" : "translate-x-1"
                                }`}
                              />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">
                      Add General Costs first to enable selection.
                    </div>
                  )}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={mobilizationConfigForm.notes}
                  onChange={(e) =>
                    setMobilizationConfigForm((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t px-6 py-4">
              <button
                type="button"
                onClick={closeMobilizationConfigModal}
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveMobilizationConfig}
                disabled={
                  savingMobilizationConfig ||
                  eligibleTeamsForMobilizationForm.length === 0
                }
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {savingMobilizationConfig
                  ? "Saving..."
                  : editingMobilizationConfigId
                    ? "Update Cost"
                    : "Save Cost"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCopyMobilizationConfigModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-5">
            <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-2">
              <span>Copy To Year</span>
              <InlineFieldTooltip
                page="mobilization_costs"
                section="modal_titles"
                fieldKey="copy_mobilization_costs_to_year"
                fallbackText="Copy all mobilization cost rows from one year to another."
                title="Copy To Year"
              />
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Copy all mobilization costs from
              <strong> {copyMobilizationConfigSourceYear}</strong> to a new
              year.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Year
            </label>
            <input
              type="number"
              value={copyMobilizationConfigTargetYear}
              onChange={(e) => {
                setCopyMobilizationConfigTargetYear(e.target.value);
                setCopyMobilizationConfigError("");
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. 2027"
            />
            {copyMobilizationConfigError && (
              <p className="mt-2 text-sm text-red-600">
                {copyMobilizationConfigError}
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  if (copyingMobilizationConfigs) return;
                  setShowCopyMobilizationConfigModal(false);
                  setCopyMobilizationConfigSourceYear(null);
                  setCopyMobilizationConfigTargetYear("");
                  setCopyMobilizationConfigError("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={copyingMobilizationConfigs}
                onClick={handleConfirmCopyMobilizationConfigs}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {copyingMobilizationConfigs ? "Copying..." : "Copy"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCopyGeneralCostModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-5">
            <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-2">
              <span>Copy To Year</span>
              <InlineFieldTooltip
                page="mobilization_costs"
                section="modal_titles"
                fieldKey="copy_general_costs_to_year"
                fallbackText="Copy all general cost rows from one year to another."
                title="Copy To Year"
              />
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Copy all general costs from
              <strong> {copyGeneralCostSourceYear}</strong> to a new year.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Year
            </label>
            <input
              type="number"
              value={copyGeneralCostTargetYear}
              onChange={(e) => {
                setCopyGeneralCostTargetYear(e.target.value);
                setCopyGeneralCostError("");
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. 2027"
            />
            {copyGeneralCostError && (
              <p className="mt-2 text-sm text-red-600">
                {copyGeneralCostError}
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  if (copyingGeneralCosts) return;
                  setShowCopyGeneralCostModal(false);
                  setCopyGeneralCostSourceYear(null);
                  setCopyGeneralCostTargetYear("");
                  setCopyGeneralCostError("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={copyingGeneralCosts}
                onClick={handleConfirmCopyGeneralCosts}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {copyingGeneralCosts ? "Copying..." : "Copy"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCopyJobModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-5">
            <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-2">
              <span>Copy To Year</span>
              <InlineFieldTooltip
                page="mobilization_costs"
                section="modal_titles"
                fieldKey="copy_jobs_to_year"
                fallbackText="Copy all job rows from one year to another."
                title="Copy To Year"
              />
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Copy all jobs from <strong>{copyJobsSourceYear}</strong> to a new
              year.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Year
            </label>
            <input
              type="number"
              value={copyJobsTargetYear}
              onChange={(e) => {
                setCopyJobsTargetYear(e.target.value);
                setCopyJobsError("");
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. 2027"
            />
            {copyJobsError && (
              <p className="mt-2 text-sm text-red-600">{copyJobsError}</p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  if (copyingJobs) return;
                  setShowCopyJobModal(false);
                  setCopyJobsSourceYear(null);
                  setCopyJobsTargetYear("");
                  setCopyJobsError("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={copyingJobs}
                onClick={handleConfirmCopyJobs}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {copyingJobs ? "Copying..." : "Copy"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCopyTeamModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-5">
            <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-2">
              <span>Copy To Year</span>
              <InlineFieldTooltip
                page="mobilization_costs"
                section="modal_titles"
                fieldKey="copy_team_configurations_to_year"
                fallbackText="Copy all team configurations from one year to another."
                title="Copy To Year"
              />
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Copy all team configurations from{" "}
              <strong>{copyTeamsSourceYear}</strong> to a new year.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Year
            </label>
            <input
              type="number"
              value={copyTeamsTargetYear}
              onChange={(e) => {
                setCopyTeamsTargetYear(e.target.value);
                setCopyTeamsError("");
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. 2027"
            />
            {copyTeamsError && (
              <p className="mt-2 text-sm text-red-600">{copyTeamsError}</p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  if (copyingTeams) return;
                  setShowCopyTeamModal(false);
                  setCopyTeamsSourceYear(null);
                  setCopyTeamsTargetYear("");
                  setCopyTeamsError("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={copyingTeams}
                onClick={handleConfirmCopyTeams}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {copyingTeams ? "Copying..." : "Copy"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCopyProductivityModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-5">
            <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-2">
              <span>Copy To Year</span>
              <InlineFieldTooltip
                page="mobilization_costs"
                section="modal_titles"
                fieldKey="copy_productivity_to_year"
                fallbackText="Copy all painter productivity rows from one year to another."
                title="Copy To Year"
              />
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Copy all productivity rows from
              <strong> {copyProductivitySourceYear}</strong> to a new year.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Year
            </label>
            <input
              type="number"
              value={copyProductivityTargetYear}
              onChange={(e) => {
                setCopyProductivityTargetYear(e.target.value);
                setCopyProductivityError("");
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. 2027"
            />
            {copyProductivityError && (
              <p className="mt-2 text-sm text-red-600">
                {copyProductivityError}
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  if (copyingProductivity) return;
                  setShowCopyProductivityModal(false);
                  setCopyProductivitySourceYear(null);
                  setCopyProductivityTargetYear("");
                  setCopyProductivityError("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={copyingProductivity}
                onClick={handleConfirmCopyProductivity}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {copyingProductivity ? "Copying..." : "Copy"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobilizationCostsManagement;
